// Boston: Old State House, "Freedom Trail by lantern light". A crisp, clear late-October night downtown, about half
// past nine. We stand on the crosswalk at the corner of State Street with Congress Street, where Devonshire Street
// splits off (42.3589 N, 71.0568 W), looking west-south-west (about 246 degrees) up State Street at the Old State
// House (1713), 40 to 73 m off: its east end faces us, a little turned, with its long north side running away to the
// right along the westbound lanes of State Street. Everything is placed from the map (OpenStreetMap footprints and
// heights) through one cylindrical projection: 5 px a degree of bearing and of elevation, the eye 1.7 m above the
// street (see the view's helpers), so every building stands at its true bearing, distance and height.
// Left to right, as seen from here: the Worthington Building (31 State) close on the left, the Second Brazer Building
// (27 State) on its corner with Devonshire Street, One Devonshire Place's pale tower behind them, 15-17 State (under
// a facade repair, its scaffold carrying the city's billboard) and 1 State under a slot of sky with the moon; the Old
// State House: the red brick east gable with the gilded lion and the silver unicorn on its corners, the round clock
// in its gold scrollwork, the balcony the Declaration of Independence was read from (18 July 1776), the oculus windows,
// the white tiered tower with its gilded dome and weathervane, the State station entrance (Orange and Blue lines) at
// the east end of its north side; One Boston Place's dark ribbed tower rising straight up behind it out of the frame,
// 26 Court Street far off in the gap, the Ames Building's pale granite (1 Court) across State Street, and the foot of
// 28 State on the right. In front: the plaza where the Boston Massacre happened (5 March 1770), its ring of
// cobblestones, the Freedom Trail's red-brick line, the westbound lanes of State Street and Devonshire Street, and the
// traffic signal on our corner with its green Congress St and Devonshire St signs.
// What can't be seen from here: the gold dome of the State House on Beacon Hill stands straight behind One Boston
// Place (bearing 257, 600 m); the harbour, Long Wharf and Faneuil Hall (two blocks north-east, up Congress Street)
// are behind the viewer. The towers around are lit only on their lowest floors by the street; a few offices work late.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody.
//
// The loop's timeline (seconds; the constants are under "The loop's timeline"):
//   6-62      a woman walks in along the north sidewalk towards us and turns off up Congress Street
//   18        a train pulls in below (a rumble); from 23 people come up the stairs of State station: two cross State
//             Street on the red towards us, two walk away along the Old State House's north side (again at 98 and 178)
//   24-40     a jogger runs out along the north sidewalk
//   30-48     inside the Old State House a night guard's torch goes from window to window (every minute)
//   35-42     the couple on our corner take a selfie with the Old State House (flash at 40)
//   48-80     a late office worker walks from our corner, crosses on the red and goes down into the T
//   50-76     BIG: a gust. The leaves start to rattle (50), a swirl of them lifts off the island and runs across the
//             plaza to the Old State House's corner (52-76), the steam is blown flat, the weathervane swings round
//   52-72     a man crosses the plaza to the station
//   88-113    BIG: the lantern tour comes round the Old State House's south-east corner behind its guide (tricorn hat,
//             colonial coat, lantern), along the Freedom Trail's red line to the Massacre ring
//   113-142   the guide talks at the ring, lantern raised, pointing up at the balcony; the group takes photos, their
//             shadows standing tall on the brick; 114-124 the floods warm up on the facade, hold, and ease off by 162
//   126-136   a cyclist rides away up State Street (bell at 128)
//   142-182   the tour crosses State Street on the red and comes past us along the north sidewalk towards Congress
//             Street; the pigeons on the corner flutter up as it passes (176-196) and settle again
//   158-214   the street sweeper crawls up State Street, its amber beacon turning across the walls
//   196-206   the couple, the rider and the man with the coffee look up at the clock
//   206-234   BIG: the clock strikes three times (206, 208.6, 211.2); the pigeons on the gable go up and wheel round
//             the tower, landing one by one by 234; the couple photograph it (flash at 211.4)
//   214-236   a dog walker crosses the island and goes away along the north side
//   all loop  the signal on our corner cycles every 40 s (green 22, amber 4, red 14) and the next one up the street
//             with it; cars set off on the green up State Street and down Devonshire Street (and on the red from
//             Congress Street down Devonshire), their headlights washing the walls ahead, their tail lights red on the
//             road; dry leaves skitter west in gusts every 8 s and a few fall at three depths; steam rises from a
//             manhole, a grate on Devonshire and the vent by us; office windows go on and off; thin cloud drifts over
//             the stars and the moon and across One Boston Place's dark glass; jets out of Logan cross the gap; the
//             delivery rider checks his phone; two friends talk by the station; the man on the island waits with his
//             coffee; the lanterns breathe; pigeons shuffle on the gable and peck on our corner
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, makePitch, gradeAd, text, glyph, FONT3 } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The view ================= */
// World metres: e east, n north of a point in the middle of State Street east of the Old State House. The camera
// stands at CAM, its eye EYE above the street; a column is a bearing (K px a degree), a row an elevation (K px a
// degree above or below the horizon H0).
const DEG = 180 / Math.PI;
const CAM = [40, 13], EYE = 1.7, K = 5, H0 = 182;
const bearingOf = (e, n) => { const b = Math.atan2(e - CAM[0], n - CAM[1]) * DEG; return b < 0 ? b + 360 : b; };
const TWR = { e: -12.5, n: -7.8 };                  // the tower's axis
const B0 = bearingOf(TWR.e, TWR.n) - 2;             // the bearing of column 240 (the tower stands at x 250)
const distOf = (e, n) => Math.hypot(e - CAM[0], n - CAM[1]);
const colOf = (e, n) => 240 + K * (bearingOf(e, n) - B0);
const rowAt = (h, d) => H0 - K * Math.atan2(h - EYE, d) * DEG;
/** screen point [x, y] of a world point */
const P = (e, n, h = 0) => [colOf(e, n), rowAt(h, distOf(e, n))];
const colBearing = x => (B0 + (x + 0.5 - 240) / K) / DEG;          // radians
const rowElev = y => (H0 - y - 0.5) / K / DEG;                      // radians
/** the street's distance under row y (below the horizon) */
const groundDist = y => EYE / Math.tan(-rowElev(y));
/** px a metre at distance d (across, and up near the horizon) */
const pxm = d => K * DEG / d;
/** world point of the street under (x, y) */
function groundAt(x, y) {
  const d = groundDist(y), b = colBearing(x);
  return [CAM[0] + d * Math.sin(b), CAM[1] + d * Math.cos(b), d];
}

/* ================= The buildings (OpenStreetMap footprints, heights from it and from the floors) ================= */
// The Old State House: walls 13 m to the eaves, ridge 19.5 m; the east gable steps up to 20.5 m, with the lion and
// the unicorn on 16 m corner blocks; the tower on the ridge's middle. Corners: the east wall S..N, the north wall E..W.
const OSH = { es: [3, -13], en: [3, -1.4], wn: [-31, -3.4], ws: [-30.5, -15], eave: 13, ridge: 19.5, rn: -8.2 };
const OSH_POLY = [OSH.en, OSH.es, OSH.ws, OSH.wn];
const BLDGS = [
  { id: 'worth', poly: [[30, -35], [28, -9], [36, -7], [43, -34]], h: 42, st: 'worth' },
  { id: 'brazer', poly: [[23, -39], [9, -37], [11, -23], [22, -17], [24.5, -16.2], [26, -17]], h: 46, st: 'brazer' },
  { id: 'b13', poly: [[22, -44], [24, -60], [8, -59], [8, -42]], h: 52, st: 'pale' },
  { id: 'newport', poly: [[6, -85], [8, -59], [24, -60], [23, -86]], h: 40, st: 'pale' },
  { id: 's1517', poly: [[-19, -48], [-17, -26], [-2, -27], [-3, -48]], h: 38, st: 'brick' },
  { id: 's1', poly: [[-27, -49], [-38, -48], [-36, -25], [-17, -26], [-19, -48]], h: 46, st: 'stone' },
  { id: 'dev1', poly: [[-19, -49], [-27, -49], [-28, -61], [-26, -75], [-19, -87], [-5, -88], [-3, -57], [-3, -48]], h: 121, st: 'dev' },
  { id: 'dev2', poly: [[-19, -75], [-31, -71], [-39, -70], [-41, -101], [-32, -107], [-18, -108]], h: 121, st: 'dev' },
  { id: 'obp', poly: [[-65, -9], [-67, -54], [-112, -51], [-110, -7]], h: 183, st: 'obp' },
  { id: 'court26', poly: [[-144, -58], [-163, -50], [-137, 15], [-119, 8]], h: 42, st: 'far' },
  { id: 'ames', poly: [[-76, 19], [-45, 10], [-44, 34], [-73, 38]], h: 56.7, st: 'ames' },
  { id: 's28', poly: [[-24, 32], [-31, 84], [-1, 88], [6, 36]], h: 152, st: 's28' },
  { id: 'osh', poly: OSH_POLY, h: OSH.eave, st: 'osh' },
];

/* ================= Streets (from the map) ================= */
// State Street's centre line (westbound, two lanes), Devonshire Street's (southbound, two lanes), both from the corner
const STATE_C = [[80, 15], [41, 9], [15, 7], [-34, 3], [-44, 4]];
const DEVON_C = [[41, 9], [25, -1], [9, -15], [5, -20], [3.5, -26], [3, -38], [2, -58]];
const STATE_HW = 3.8, DEVON_HW = 3.5;               // half widths of the carriageways
const RING = { e: 11, n: -2, r: 1.4 };              // the Boston Massacre site: a ring of cobblestones in a bronze band
const ACCENT = '#44bb11';                           // the Green Line's green

/* ================= Pixel helpers (after boston.js) ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, v], ...] sorted by t; the value at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { v: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { v: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { v: keys[keys.length - 1][1], moving: false };
}
/** a point along a polyline of world points, s metres from its start */
function polyAt(pts, s) {
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1], [bx, by] = pts[i], l = Math.hypot(bx - ax, by - ay);
    if (s <= l || i === pts.length - 1) { const u = s / l; return [lerp(ax, bx, u), lerp(ay, by, u), Math.atan2(bx - ax, by - ay)]; }
    s -= l;
  }
  return pts[pts.length - 1];
}
const polyLen = pts => pts.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0);
/** distance from (e, n) to a polyline, and the side (+1 left of its direction, -1 right) */
function polyDist(pts, e, n) {
  let best = 1e9, side = 1;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1], [bx, by] = pts[i], dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const u = clamp(((e - ax) * dx + (n - ay) * dy) / l2, 0, 1), px2 = ax + u * dx, py2 = ay + u * dy, dd = Math.hypot(e - px2, n - py2);
    if (dd < best) { best = dd; side = (dx * (n - ay) - dy * (e - ax)) > 0 ? 1 : -1; }
  }
  return [best, side];
}
const inWin = (t, a, b) => t >= a && t < b;

/* ================= The sky: a cold clear night, seen only through the gaps between the towers ================= */
// Deep blue overhead, paling towards the horizon into the city's orange glow. The moon, a day past first quarter,
// stands in the south-west over 15-17 State (bearing 230, 29 degrees up), lit on its right.
const SKY = ['#04061a', '#070a20', '#0a0e27', '#0e132f', '#121838', '#171e42', '#1d254b', '#252c53', '#2f345a', '#3b3b5e',
  '#4a4460', '#5a4c5e', '#6a545a'];
const skyV = (x, y) => Math.pow(clamp(y / H0, 0, 1), 1.8) * 0.9 + 0.08 * Math.exp(-(((x - 158) / 40) ** 2) - (((y - 14) / 30) ** 2));
const MOON = { x: 158, y: 14, r: 4 };
function renderSky() {
  const p = pix(W, H0 + 8);
  for (let y = 0; y < H0 + 8; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, y) + (vnoise(x / 60, y / 12, 3) - 0.5) * 0.04, x, y));
  const { x: mx, y: my, r } = MOON;                                                          // the moon: lit on the right, a little gibbous
  for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) {
    const q = (i * i + j * j) / (r * r + r * 0.6);
    if (q > 1) continue;
    const term = -0.25 * r * Math.sqrt(Math.max(0, 1 - (j / r) ** 2));                      // the terminator, left of centre
    const lit = i > term, mare = hash(i + 9, j + 9, 41) < 0.18 && q < 0.7;
    p.set(mx + i, my + j, lit ? (mare ? '#c8c4b0' : q > 0.75 ? '#e4e0cc' : '#f6f2de') : '#1c2038');
  }
  return p.done();
}
/* thin cirrus, tileable (period W): faint streaks catching the city's glow underneath */
function makeWisps(seed, h, banks, col, colLit, a) {
  const r = rng(seed), streaks = [];
  for (let b = 0; b < banks; b++) {
    const bx0 = r() * W, by = 4 + r() * (h - 8), n = 3 + Math.floor(r() * 4);
    for (let i = 0; i < n; i++) streaks.push({ x: bx0 + (r() - 0.5) * 50, y: by + (r() - 0.5) * 6, rx: 18 + r() * 40, ry: 1.2 + r() * 2 });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0, lo = 0;
    for (const s of streaks) {
      let dx = x - s.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / s.rx) ** 2 - ((y - s.y) / s.ry) ** 2;
      if (q > v) { v = q; lo = y > s.y ? 1 : 0; }
    }
    v *= 0.75 + 0.5 * vnoise(x / 9, y / 2, seed);
    if (v > 0.12 + bayer(x, y) * 0.5) p.set(x, y, lo && v > 0.35 ? colLit : col, a);
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
const WINS = [];                                    // { x, y, w, h, c, k, ph, duty }: lit while frac(k t / LOOP + ph) < duty
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0', '#b8cce4'];

/* ================= The towers around: a raycast of their footprints ================= */
// Each column casts a ray; every footprint it crosses is painted near to far (a building hides what stands behind),
// from its roof row down to its foot, with a facade worked out from where the ray meets the wall: u metres along the
// wall and the height h of each row there. So every window, floor and cornice sits in true perspective.
/** nearest crossing of the column-x ray with a footprint: { d, i (edge), u (m along it), len, nx, ny (outward normal) } */
function hitWall(poly, x) {
  const b = colBearing(x), dx = Math.sin(b), dy = Math.cos(b);
  let best = null;
  for (let i = 0; i < poly.length; i++) {
    const [ax, ay] = poly[i], [bx2, by2] = poly[(i + 1) % poly.length], ex = bx2 - ax, ey = by2 - ay;
    const det = ex * dy - dx * ey;
    if (Math.abs(det) < 1e-9) continue;
    const qx = ax - CAM[0], qy = ay - CAM[1];
    const t = (ex * qy - ey * qx) / det, s = (dx * qy - dy * qx) / det;
    if (t > 0.5 && s >= 0 && s <= 1 && (!best || t < best.d)) {
      const len = Math.hypot(ex, ey);
      best = { d: t, i, u: s * len, len, ex: ex / len, ey: ey / len };
    }
  }
  if (best) {                                       // the outward normal: the side of the edge the camera is on
    const nx = best.ey, ny = -best.ex, [ax, ay] = poly[best.i];
    const toCam = (CAM[0] - ax) * nx + (CAM[1] - ay) * ny;
    best.nx = toCam > 0 ? nx : -nx; best.ny = toCam > 0 ? ny : -ny;
  }
  return best;
}
// The facades: ramp (night dark to street-lit), floors (ground floor height, floor height), bays (width, first one's
// offset), the window's share of a bay and of a floor, the lit share and colours, extras
const STYLES = {
  worth: { ramp: ['#16151c', '#211f26', '#2e2a30', '#3e3836', '#544a42', '#6e604e', '#8c7a60'], g: 5, fh: 3.9, bw: 2.4, wf: 0.5, hf: 0.56, lit: 0.06, cool: 0.6, shop: '#ffcf86', arch: 9 },
  brazer: { ramp: ['#18171c', '#232127', '#322e30', '#45403c', '#5e5648', '#7c705a', '#9c8c70'], g: 5.2, fh: 3.8, bw: 2.2, wf: 0.52, hf: 0.58, lit: 0.07, cool: 0.7, shop: '#e8eef8', arch: 10 },
  pale: { ramp: ['#14151c', '#1e1f26', '#2a2a31', '#38373c', '#4a4846', '#605c56'], g: 4.6, fh: 3.7, bw: 2.0, wf: 0.5, hf: 0.55, lit: 0.08, cool: 0.6 },
  brick: { ramp: ['#16141a', '#221d22', '#30282a', '#433632', '#5a473c', '#745a46'], g: 4.6, fh: 3.6, bw: 2.3, wf: 0.48, hf: 0.55, lit: 0.07, cool: 0.5, band: 3 },
  stone: { ramp: ['#14151b', '#1e2027', '#2a2c33', '#393a40', '#4c4c50', '#626062'], g: 5, fh: 3.7, bw: 2.0, wf: 0.55, hf: 0.58, lit: 0.08, cool: 0.7 },
  dev: { ramp: ['#141419', '#1d1d22', '#28282c', '#353437', '#454342', '#5a564e'], g: 6, fh: 2.9, bw: 1.7, wf: 0.62, hf: 0.5, lit: 0.16, cool: 0.25 },
  obp: { ramp: ['#060609', '#0a0a0e', '#0f0f13', '#16151a', '#1e1c21'], g: 6.5, fh: 3.95, bw: 1.55, wf: 0.8, hf: 0.7, lit: 0.05, cool: 0.3, ribs: true },
  far: { ramp: ['#101119', '#16171f', '#1d1e26', '#25262e'], g: 4.5, fh: 3.6, bw: 2.2, wf: 0.5, hf: 0.5, lit: 0.18, cool: 0.5 },
  ames: { ramp: ['#17161c', '#222027', '#302c32', '#423c40', '#585050', '#726862', '#8e8478'], g: 4.6, fh: 3.85, bw: 2.2, wf: 0.46, hf: 0.56, lit: 0.34, cool: 0.15, arcade: true },
  s28: { ramp: ['#0d0e12', '#14151a', '#1c1d23', '#25262d', '#30313a'], g: 7, fh: 3.7, bw: 1.6, wf: 0.6, hf: 0.62, lit: 0.06, cool: 0.8, lobby: true },
};
const streetGlow = (h, d) => 0.36 * Math.exp(-Math.max(0, h - 1.5) / 5) * clamp(60 / d, 0.4, 1.0);
/** the colour of a facade pixel; records the windows that switch */
function facade(B, hit, hh, x, y, rec) {
  const S = STYLES[B.st], { u, d, len } = hit;
  const ramp = S.ramp;
  // faces turned to the moon (south-west) a touch lighter, faces in its shadow darker; the street's light on the base
  const moonF = (hit.nx * -0.77 + hit.ny * -0.64) * 0.08, edgeF = (u < 0.35 || len - u < 0.35) ? 0.1 : 0;
  let v = 0.2 + moonF + edgeF + streetGlow(hh, d) + (vnoise(u / 3, hh / 4, B.id.length * 7) - 0.5) * 0.04;
  const top = B.h - hh;
  if (top < 0.6) v += 0.16;                                                                   // the cornice or parapet catching the sky
  else if (top < 1.6 && B.st !== 'obp' && B.st !== 's28') v -= 0.1;                          // the shadow under it
  const fl = hh < S.g ? -1 : Math.floor((hh - S.g) / S.fh), fy = hh < S.g ? hh / S.g : (hh - S.g) / S.fh - fl;
  const bay = Math.floor(u / S.bw), bx = u / S.bw - bay;
  if (S.band && fl >= 0 && fl % S.band === 0 && fy < 0.12) v += 0.08;                         // stone bands every few floors
  if (S.ribs && bx < 0.14) {                                                                  // One Boston Place's ribs: bronze mullions
    v += 0.02 + streetGlow(hh, d) * 0.5;
    return pick(['#0c0b0c', '#141210', '#1c1914', '#28221a', '#3a3022'], v, x, y, 0.6);
  }
  const inWinX = Math.abs(bx - 0.5) < S.wf / 2, inWinY = fl >= 0 && Math.abs(fy - 0.5) < S.hf / 2 && top > 1.2;
  if (fl < 0) {                                                                               // the ground floor: shops, lobbies
    if (S.shop && hh > 0.4 && hh < S.g - 1.1 && Math.abs(bx - 0.5) < 0.38) {
      const lit = hash(B.id.length, bay, 3) < 0.6, mull = Math.abs(bx - 0.5) < 0.03 || hh > S.g - 1.5;
      if (mull) return hh > S.g - 1.5 ? pick(ramp, v - 0.1, x, y, 0.3) : '#1a1614';
      return lit ? mixHex(S.shop, '#2a2018', 0.55 + 0.25 * (hh / S.g)) : '#121216';
    }
    if (S.lobby && hh < S.g - 1 && bx > 0.1) return mixHex('#ffe0a8', '#5a4a38', 0.45 + 0.3 * hash(x, y, 9));
    if (S.arcade && hh < S.g - 0.9 && Math.abs(bx - 0.5) < 0.34) return hh > S.g - 1.8 && Math.abs(bx - 0.5) > 0.22 ? pick(ramp, v + 0.1, x, y) : '#2a2018';
    return pick(ramp, v + 0.08, x, y, 0.6);
  }
  let arched = false;
  if (S.arcade) {                                                                             // the Ames: a two-storey arcade under the cornice
    const floors = Math.floor((B.h - S.g) / S.fh);
    if (fl >= floors - 2) {
      const ax = Math.abs(bx - 0.5), ty = (hh - (S.g + (floors - 2) * S.fh)) / (2 * S.fh);
      if (ax < 0.36 && ty > 0.06) arched = ty < 0.86 - (ax > 0.25 ? (ax - 0.25) * 2 : 0);   // round-headed: the top corners cut
      if (!arched) return pick(ramp, v + (ty > 0.9 ? 0.12 : 0), x, y, 0.6);
    }
    if (fl === 0 || fl === 1) v += (Math.floor(hh / 0.9) % 2 ? 0.05 : -0.03);                  // rusticated granite on the lower floors
  }
  if (S.arch && fl === Math.floor((B.h - S.g) / S.fh) - 1 && inWinX && fy > 0.2) {            // round-headed windows under the cornice
    const ax = (bx - 0.5) / (S.wf / 2), cap = 0.78 - 0.18 * ax * ax;
    if (fy > cap) return pick(ramp, v, x, y, 0.6);
  }
  if (inWinX && fl >= 0 && top > 1.2 && !S.ribs) {                                           // the sill under the window, the lintel over it
    const sill = Math.abs(fy - (0.5 - S.hf / 2) + 0.03) < 0.045, lint = Math.abs(fy - (0.5 + S.hf / 2) - 0.035) < 0.04;
    if (sill || lint) return pick(ramp, v + (sill ? 0.16 : 0.1), x, y, 0.35);
  }
  if (arched || (inWinX && inWinY)) {
    const gb = S.ribs ? Math.floor(bay / 4) : bay;                                            // One Boston Place: an office lights a few bays together
    const key = `${B.id}|${hit.i}|${fl}|${bay}`, hv = hash(fl * 131 + gb, hit.i * 17 + B.id.length, B.id.charCodeAt(0));
    const lit = hv < S.lit, tog = !lit && hv < S.lit + 0.15;
    const col = (hv * 977 % 1) < S.cool ? COOLW[Math.floor(hv * 91 % 4)] : WARMW[Math.floor(hv * 53 % 4)];
    if (tog && rec) {
      const w = rec.get(key);
      if (w) { w.x0 = Math.min(w.x0, x); w.x1 = Math.max(w.x1, x); w.y0 = Math.min(w.y0, y); w.y1 = Math.max(w.y1, y); }
      else rec.set(key, { x0: x, x1: x, y0: y, y1: y, c: mixHex(col, '#141824', clamp(d / 260, 0, 0.4)), hv });
    }
    if (lit) return mixHex(col, '#141824', clamp(d / 260, 0, 0.45) + (fy > 0.82 ? 0.25 : 0));
    // a dark window: the sky's blue in the glass, a little of the street's light low down
    return pick(['#07080e', '#0c0f18', '#121724', '#1a2030', '#262c3c'], 0.28 + streetGlow(hh, d) * 0.8 + (B.st === 'obp' ? -0.1 : 0) + (fy > 0.75 ? 0.1 : 0), x, y, 0.7);
  }
  return pick(ramp, v, x, y, d < 60 ? 0.3 : 0.6);
}
/** paint the towers near to far; returns the canvas, the foot row per column and the toggling windows */
function renderTowers() {
  const p = pix(W, H), taken = new Uint8Array(W * H), foot = new Float32Array(W).fill(H0 + 1), rec = new Map(), glass = pix(W, H);
  for (let x = 0; x < W; x++) {
    const hits = [];
    for (const B of BLDGS) { const h = hitWall(B.poly, x); if (h) hits.push([B, h]); }
    hits.sort((a, b) => a[1].d - b[1].d);
    if (hits.length) foot[x] = rowAt(0, hits[0][1].d);
    for (const [B, hit] of hits) {
      const top = Math.max(0, Math.ceil(rowAt(B.h, hit.d))), base = Math.min(H - 1, Math.floor(rowAt(0, hit.d)));
      if (B.st === 'osh') { for (let y = top; y <= base; y++) taken[y * W + x] = 1; continue; }   // drawn on its own layer
      for (let y = top; y <= base; y++) {
        if (taken[y * W + x]) continue;
        taken[y * W + x] = 1;
        const hh = EYE + hit.d * Math.tan(rowElev(y));
        p.set(x, y, facade(B, hit, hh, x, y, rec));
        if (B.st === 'obp' && hh > STYLES.obp.g) glass.set(x, y, '#ffffff');
      }
    }
  }
  for (const w of rec.values()) WINS.push({ x: w.x0, y: w.y0, w: w.x1 - w.x0 + 1, h: w.y1 - w.y0 + 1, c: w.c, k: 2 + Math.floor(w.hv * 7919 % 5), ph: w.hv * 31 % 1, duty: 0.14 + (w.hv * 613 % 1) * 0.22 });
  return { c: p.done(), foot, glass: glass.done() };
}

/* ================= The Old State House ================= */
// Red brick in a warm floodlight, white-painted trim, a slate roof, the white tower. The east gable steps up in two
// curved steps a side to a capped top; the lion (gold) and the unicorn (silver) stand rampant on its corner blocks.
// East wall: uS metres from its south (left) corner; north wall: v metres from its east end.
const BRICK = ['#1e0e10', '#2e1412', '#421a14', '#5a2216', '#742c1a', '#8e381e', '#a84624', '#c05a2e', '#d4743c', '#e6965a'];
const TRIM = ['#3a3440', '#5c5452', '#867c70', '#aea290', '#d4c8b0', '#f0e4c8', '#fff8e4'];
const SLATE = ['#0e1018', '#151822', '#1d212c', '#262b36', '#313744'];
const GOLD = ['#3a2408', '#6a4410', '#9a6a18', '#c89426', '#e8b83c', '#fad866', '#fff0a8'];
const SILVER = ['#3a3c46', '#5e606c', '#868894', '#b0b2bc', '#d8dae2', '#f4f6fa'];
/** the east gable's top (m) at uS: corner blocks at 16, a curved step up to 18, a flat step, a second curve to 19.8
    and the capped middle to 20.5 */
function gableTop(uS) {
  const u = uS <= 5.8 ? uS : 11.6 - uS;
  if (u < 0 || u > 5.8) return 13;
  if (u < 1.25) return 16;
  if (u < 2.45) return 16 + 2 * smooth(1.25, 2.45, u);                                       // an S-curved shoulder
  if (u < 3.05) return 18;
  if (u < 4.3) return 18 + 1.8 * smooth(3.05, 4.3, u);
  return 19.8 + 0.7 * smooth(4.3, 5.8, u);
}
const CLOCK = { u: 5.8, h: 17.3, r: 1.0 };
// the floods: uplights at the foot of the east wall, a lamp on a pole across the plaza for the gable, and the north
// side's floods along the sidewalk; warm makes them all burn brighter (the warm-up, a big moment)
function eastLight(uS, hh, warm) {
  const fan = [1.7, 5.8, 9.9].reduce((s, f) => s + Math.exp(-(((uS - f) / (1.3 + hh * 0.12)) ** 2)), 0);
  const up = Math.exp(-Math.max(0, hh - 2) / (9 + warm * 4));
  return 0.34 + 0.3 * fan * up + 0.16 * smooth(11, 16, hh) + warm * (0.12 + 0.1 * fan * up);
}
function northLight(v, hh, warm) {
  const fan = Math.exp(-(((frac(v / 8.5 + 0.3) - 0.5) / 0.22) ** 2));
  return 0.24 + 0.22 * fan * Math.exp(-Math.max(0, hh - 1.5) / 7) + 0.08 * smooth(8, 13, hh) + warm * 0.1;
}
/** a 12-over-12 sash window at (a, b) local coords inside its opening (0..1 each): frame, muntins, dark glass */
function sash(a, b, x, y, lit) {
  if (a < 0.1 || a > 0.9 || b < 0.05 || b > 0.95) return pick(TRIM, 0.62 + lit * 0.3, x, y, 0.5);
  if (Math.abs(a - 0.5) < 0.07 || Math.abs(b - 0.5) < 0.04) return pick(TRIM, 0.5 + lit * 0.3, x, y, 0.5);
  return pick(['#0a0a12', '#121420', '#1c1e2c', '#2c2a34', '#4a3a34'], 0.2 + lit * 0.25 + (b > 0.78 ? 0.22 : 0) * lit, x, y, 0.7);
}
/** the east wall and gable at (uS, hh): a colour or null (open sky above the gable) */
function eastTex(uS, hh, x, y, warm) {
  const top = gableTop(uS);
  if (hh > top) return null;
  const L = eastLight(uS, hh, warm), lit = clamp(L, 0, 1);
  if (top - hh < 0.28) return pick(TRIM, 0.55 + lit * 0.4, x, y, 0.5);                        // the gable's coping
  if (Math.abs(hh - 13) < 0.22 || (hh > 6.15 && hh < 6.5)) return pick(TRIM, 0.45 + lit * 0.4, x, y, 0.5);   // belt courses
  if (hh < 0.45) return pick(['#1a1820', '#2a2830', '#3c3a40', '#55525a'], 0.3 + lit * 0.4, x, y);   // the granite plinth
  // the clock in its gold scrollwork: a dark face, a gold ring; the hands are drawn per frame
  const cu = (uS - CLOCK.u) / CLOCK.r, ch = (hh - CLOCK.h) / CLOCK.r, cr = Math.hypot(cu, ch);
  if (cr < 1) return cr > 0.86 ? pick(GOLD, 0.7 + 0.2 * ch, x, y) :
    (cr > 0.64 && Math.abs(frac(Math.atan2(cu, ch) / TAU * 12 + 0.5) - 0.5) < 0.12 ? GOLD[4] : pick(['#0a0e1c', '#121830', '#1c2440'], 0.35 + 0.3 * (1 - cr), x, y));
  // gilded acanthus scrolls: a curl either side of the face, a crest over it
  const sideU = Math.abs(cu) - 1.45, sc = Math.hypot(sideU / 0.55, (ch + 0.1) / 0.75);
  if (sc < 1 && hh > 15.4) { const curl = Math.sin(Math.atan2(sideU, ch + 0.1) * 2 + sc * 5); if (curl > -0.2 || sc > 0.75) return pick(GOLD, 0.5 + 0.35 * curl + lit * 0.1, x, y, 0.7); }
  const crest = Math.hypot(cu / 0.9, (ch - 1.25) / 0.38);
  if (crest < 1) return pick(GOLD, 0.55 + 0.3 * (1 - crest), x, y, 0.7);
  // openings: the attic's oculi and window, the balcony door in its white surround, the windows, basement lights
  const ocL = Math.hypot((uS - 2.2) / 0.62, (hh - 14.1) / 0.62), ocR = Math.hypot((uS - 9.4) / 0.62, (hh - 14.1) / 0.62);
  const oc = Math.min(ocL, ocR);
  if (oc < 1) return oc > 0.72 ? pick(TRIM, 0.55 + lit * 0.4, x, y) : (Math.abs(frac((Math.atan2(uS - (ocL < ocR ? 2.2 : 9.4), hh - 14.1) / TAU) * 8) - 0.5) > 0.42 ? pick(TRIM, 0.45 + lit * 0.3, x, y) : pick(['#0a0a12', '#141624', '#2a2430'], 0.3 + lit * 0.2, x, y));
  if (uS > 5.05 && uS < 6.55 && hh > 13.25 && hh < 15.3) return sash((uS - 5.05) / 1.5, (hh - 13.25) / 2.05, x, y, lit);
  if (uS > 4.5 && uS < 7.1 && hh > 6.6 && hh < 12.7) {                                        // the doorway: pilasters, hood, fanlight
    const inD = uS > 5.0 && uS < 6.6 && hh < 11.5;
    if (hh > 11.6) { const hood = 12.7 - 0.9 * ((uS - 5.8) / 1.3) ** 2; if (hh < hood) return pick(TRIM, 0.6 + lit * 0.35 + (hh > hood - 0.25 ? 0.15 : 0), x, y, 0.5); }
    else if (!inD) return pick(TRIM, 0.48 + lit * 0.4 + (uS < 4.75 || (uS > 6.6 && uS < 6.85) ? 0.12 : 0), x, y, 0.5);
    else if (hh > 10.4) return Math.abs(frac((Math.atan2(uS - 5.8, hh - 10.4) / TAU) * 10) - 0.5) > 0.4 ? pick(TRIM, 0.55 + lit * 0.3, x, y) : '#2a2026';
    else return sash((uS - 5.0) / 1.6, (hh - 6.6) / 3.8, x, y, lit * 1.2);
  }
  for (const [u0, u1] of [[1.5, 2.9], [8.7, 10.1]]) {
    if (uS > u0 && uS < u1 && hh > 7.6 && hh < 11.3) return sash((uS - u0) / (u1 - u0), (hh - 7.6) / 3.7, x, y, lit);
    if (uS > u0 && uS < u1 && hh > 2.9 && hh < 5.85) return sash((uS - u0) / (u1 - u0), (hh - 2.9) / 2.95, x, y, lit);
    if (uS > u0 + 0.15 && uS < u1 - 0.15 && hh > 0.75 && hh < 1.85) return sash((uS - u0 - 0.15) / (u1 - u0 - 0.3), (hh - 0.75) / 1.1, x, y, lit * 0.8);
  }
  if (uS > 5.1 && uS < 6.5 && hh > 2.9 && hh < 5.85) return sash((uS - 5.1) / 1.4, (hh - 2.9) / 2.95, x, y, lit);
  // Flemish bond: a header's darker end every other brick, the mortar's courses
  const course = Math.floor(hh / 0.26), hdr = (Math.floor(uS / 0.31) + course) % 2 === 0 && hash(Math.floor(uS / 0.31), course, 5) < 0.6;
  return pick(BRICK, L + (hdr ? -0.07 : 0) + (vnoise(uS / 1.4, hh / 1.1, 21) - 0.5) * 0.08 - (uS < 0.25 || uS > 11.35 ? 0.06 : 0), x, y, 0.7);
}
const T_DOOR = { v0: 1.0, v1: 2.5, h: 2.5 };        // the State station entrance at the east end of the north side
/** the north wall at (v from its east end, hh) */
function northTex(v, hh, x, y, warm) {
  const L = northLight(v, hh, warm), lit = clamp(L, 0, 1);
  if (hh > 12.6) return pick(TRIM, 0.4 + lit * 0.4 + (hh > 12.85 ? 0.12 : 0), x, y, 0.5);    // the cornice
  if (hh > 6.15 && hh < 6.45) return pick(TRIM, 0.35 + lit * 0.4, x, y, 0.5);
  if (hh < 0.4) return pick(['#1a1820', '#2a2830', '#3c3a40'], 0.3 + lit * 0.4, x, y);
  if (v > T_DOOR.v0 && v < T_DOOR.v1 && hh < T_DOOR.h) return hh > T_DOOR.h - 0.2 ? pick(TRIM, 0.6, x, y) : pick(['#6a4a2a', '#a8784a', '#e0b070', '#fff0c8'], 0.35 + 0.5 * (1 - hh / T_DOOR.h) + (hash(x, y, 7) - 0.5) * 0.2, x, y);
  if (v > 14.1 && v < 17 && hh < 4.8) {                                                       // the north door's porch
    const inD = v > 14.8 && v < 16.3 && hh < 3.9;
    return inD ? (hh > 3.2 ? pick(TRIM, 0.5, x, y) : pick(['#1a1218', '#2a1c1e', '#3a2620'], 0.4, x, y)) : pick(TRIM, 0.5 + lit * 0.3, x, y, 0.5);
  }
  const bv = (v - 1.0) / 4.25, bay = Math.floor(bv), bx = bv - bay;
  if (bay >= 0 && bay < 8 && Math.abs(bx - 0.5) < 0.17 && Math.abs(v - 15.55) > 1.6) {
    const a = (bx - 0.33) / 0.34;
    if (hh > 7.7 && hh < 11.2) return sash(a, (hh - 7.7) / 3.5, x, y, lit);
    if (hh > 3.0 && hh < 5.9 && !(v < T_DOOR.v1 + 0.6)) return sash(a, (hh - 3.0) / 2.9, x, y, lit);
    if (hh > 0.7 && hh < 1.75 && !(v < T_DOOR.v1 + 0.6)) return sash(a, (hh - 0.7) / 1.05, x, y, lit * 0.6);
  }
  if (Math.abs(v - 12.6) < 0.12 && hh < 12.6) return '#1a1418';                               // a downspout
  const course = Math.floor(hh / 0.26), hdr = (Math.floor(v / 0.31) + course) % 2 === 0;
  return pick(BRICK, L + (hdr ? -0.06 : 0) + (vnoise(v / 1.4, hh / 1.1, 22) - 0.5) * 0.08, x, y, 0.7);
}
/** where a column's ray crosses a segment [a, b] of world points: distance along the ray, or -1 */
function rayCross(x, a, b) {
  const t = colBearing(x), dx = Math.sin(t), dy = Math.cos(t), ex = b[0] - a[0], ey = b[1] - a[1], det = ex * dy - dx * ey;
  if (Math.abs(det) < 1e-9) return -1;
  const qx = a[0] - CAM[0], qy = a[1] - CAM[1], d = (ex * qy - ey * qx) / det, s = (dx * qy - dy * qx) / det;
  return s >= 0 && s <= 1 && d > 0 ? d : -1;
}
const RIDGE = [[3, -7.2], [-30.75, -9.2]];
// the tower's stages: [half width (m), from, to, kind]; the lantern is an octagon
const TOWER = [[2.3, 19.0, 22.7, 'base'], [2.4, 22.7, 23.15, 'cornice'], [2.0, 23.15, 28.0, 'arch'], [2.1, 28.0, 28.7, 'bal'],
  [1.45, 28.7, 31.2, 'lantern']];
const square = (hw, oct = false) => oct ? Array.from({ length: 8 }, (_, k) => [TWR.e + hw * Math.cos(k * TAU / 8 + TAU / 16), TWR.n + hw * Math.sin(k * TAU / 8 + TAU / 16)])
  : [[TWR.e + hw, TWR.n + hw], [TWR.e + hw, TWR.n - hw], [TWR.e - hw, TWR.n - hw], [TWR.e - hw, TWR.n + hw]];
const DORMERS = [7.4, 23.6, 15.55];                 // v of the north slope's dormers (the middle one in front of the tower)
function paintTower(p, warm) {
  for (const [hw, h0, h1, kind] of TOWER) {
    const poly = square(hw, kind === 'lantern');
    for (let x = 200; x < 300; x++) {
      const hit = hitWall(poly, x);
      if (!hit) continue;
      const y0 = Math.ceil(rowAt(h1, hit.d)), y1 = Math.floor(rowAt(h0, hit.d)), a = hit.u / hit.len;
      const east = Math.abs(hit.nx) > Math.abs(hit.ny);                                    // the east face is the lit one
      for (let y = y0; y <= y1; y++) {
        const hh = EYE + hit.d * Math.tan(rowElev(y)), q = (hh - h0) / (h1 - h0);
        let v = (east ? 0.62 : 0.42) + 0.25 * (1 - q) + warm * 0.12 - (a < 0.08 || a > 0.92 ? 0.1 : 0);
        if (kind === 'cornice') v += q > 0.5 ? 0.2 : -0.15;
        if (kind === 'bal') v += (Math.floor(a * 12) % 2 && q < 0.75 && q > 0.2) ? -0.35 : 0.1;
        if (kind === 'base' && Math.abs(a - 0.5) < 0.14 && q > 0.35 && q < 0.8) v = 0.15;          // a small window
        if (kind === 'arch') {                                                                // the big round-headed window
          const ax = (a - 0.5) / 0.27, cap = 0.78 + 0.16 * Math.sqrt(Math.max(0, 1 - ax * ax));
          if (Math.abs(ax) < 1 && q > 0.12 && q < cap) v = (Math.abs(ax) < 0.12 || Math.abs(q - 0.62) < 0.03) ? v - 0.12 : 0.12 + (q > 0.7 ? 0.05 : 0);
          if (a < 0.13 || a > 0.87) v += 0.08;                                                // corner pilasters
        }
        if (kind === 'lantern' && q > 0.15 && q < 0.8 - (Math.abs(a - 0.5) > 0.18 ? 0.12 : 0) && Math.abs(a - 0.5) < 0.28) v = 0.14 + 0.08 * (q < 0.45 ? 1 : 0);   // an arched opening on every face
        if (kind === 'base' || (kind === 'arch' && q < 0.3) || kind === 'cornice') v -= 0.16 * (1 - warm * 0.5);   // low on the tower, in the gable's shade
        p.set(x, y, v < 0.25 ? pick(['#16121a', '#2a2024', '#4a3628'], v * 3, x, y) : pick(TRIM, v, x, y, 0.6));
      }
    }
  }
  // the urns on the corners of the base and of the balustrade
  for (const [hw, hh] of [[2.45, 23.15], [2.3, 28.7]]) for (const [se, sn] of [[1, 1], [1, -1], [-1, 1]]) {
    const [ux, uy] = P(TWR.e + se * hw, TWR.n + sn * hw, hh);
    p.set(ux, uy - 1, TRIM[5]); p.set(ux, uy - 2, TRIM[6]); p.set(ux, uy, TRIM[4]);
  }
  // the gilded dome: a half ellipse over the lantern
  const [dx, dy] = P(TWR.e, TWR.n, 31.2), rx = 1.35 * pxm(distOf(TWR.e, TWR.n)), ry = rx * 0.82;
  for (let j = -Math.ceil(ry); j <= 0; j++) for (let i = -Math.ceil(rx); i <= Math.ceil(rx); i++) {
    const q = (i / rx) ** 2 + (j / ry) ** 2;
    if (q > 1) continue;
    p.set(dx + i, dy + j, pick(GOLD, 0.95 - (i + rx) / (2 * rx) * 0.55 - (j / ry) * -0.1 + warm * 0.05, dx + i, dy + j, 0.6));
  }
  const [bx, by] = P(TWR.e, TWR.n, 32.7);                                                    // the finial's gilded ball and rod
  p.set(bx, by, GOLD[5]); p.set(bx - 1, by, GOLD[4]); p.set(bx, by + 1, GOLD[3]); p.set(bx - 1, by + 1, GOLD[3]);
  for (let y = Math.round(P(TWR.e, TWR.n, 35.2)[1]); y < by; y++) p.set(bx, y, GOLD[4]);
}
/** the north slope of the roof: slate between the eaves and the ridge, a lit edge along the ridge */
function paintRoof(p) {
  for (let x = 180; x < 300; x++) {
    const hit = hitWall(OSH_POLY, x);
    if (!hit) continue;
    const dr = rayCross(x, RIDGE[0], RIDGE[1]);
    if (dr < 0) continue;
    const y0 = Math.ceil(rowAt(OSH.ridge, dr)), y1 = Math.floor(rowAt(OSH.eave, hit.d));
    for (let y = y0; y <= y1; y++) p.set(x, y, pick(SLATE, 0.45 + (y === y0 ? 0.45 : 0) + ((y - y0) % 2 ? -0.06 : 0.04) + (hash(x >> 1, y, 61) - 0.5) * 0.1, x, y, 0.6));
  }
}
function paintDormers(p) {
  for (const v of DORMERS) {
    const s = v / 34.06, e = lerp(OSH.en[0], OSH.wn[0], s), n = lerp(OSH.en[1], OSH.wn[1], s) - 1.7, hw = 0.85;
    const L = P(e + hw, n, 14.9), Rr = P(e - hw, n, 14.9), Lt = P(e + hw, n, 16.6), Rt = P(e - hw, n, 16.6), Ap = P(e, n, 17.5);
    const xa = Math.round(Math.min(L[0], Rr[0])), xb = Math.round(Math.max(L[0], Rr[0]));
    for (let x = xa; x <= xb; x++) {
      const u = xb > xa ? (x - xa) / (xb - xa) : 0.5, yb = lerp(L[1], Rr[1], u), yt = lerp(Lt[1], Rt[1], u), ya = Math.min(yt, lerp(Lt[1], Ap[1], 1 - Math.abs(u - 0.5) * 2));
      for (let y = Math.round(ya); y <= Math.round(yb); y++) {
        const win = Math.abs(u - 0.5) < 0.28 && y > yt + 1 && y < yb - 1;
        p.set(x, y, win ? (hash(v * 10, 3) < 0.4 ? '#c8a070' : '#14141c') : y < yt ? pick(TRIM, 0.5, x, y) : pick(TRIM, 0.4, x, y));
      }
    }
  }
}
// the lion (gold) and the unicorn (silver, gilded horn), rampant on the gable's corner blocks, facing out
const LION = ['..gg......', '.gGGg.....', 'gGGGGg....', 'gGgGGg....', '.g.gGGg...', '...gGGGg..', '..gGGGGgg.', '..ggGGGg.g',
  '...gGGg..g', '...gg.gg.g', '..gg..gg..', '.ggg.ggg..'];
const UNICORN = ['........h..', '.......h...', '......sSs..', '.....sSSSd.', '....sSSd...', '...sSSSd...', '..sSSSSSd..',
  '.sSSSSS.Sd.', 'sd.SSSSd.d.', 's..sSSd....', '...sd.sd...', '..sd...sd..', '.ssd..ssd..'];
function paintFigures(p, warm) {
  const put = (rows, key, cx, fy, flip) => {
    const h = rows.length, w = rows[0].length;
    rows.forEach((row, j) => { for (let i = 0; i < w; i++) { const k = key[row[i]]; if (k) p.set(cx - (w >> 1) + (flip ? w - 1 - i : i), fy - h + j, k); } });
  };
  const gk = { g: GOLD[2 + (warm > 0.5 ? 1 : 0)], G: GOLD[5] }, sk = { s: SILVER[3], S: SILVER[5], d: SILVER[1], h: GOLD[5] };
  const [lx, ly] = P(3, OSH.es[1] + 0.62, 16), [ux, uy] = P(3, OSH.en[1] - 0.62, 16);
  put(LION, gk, lx, ly, false);
  put(UNICORN, sk, ux, uy, false);
  return { lion: [lx, ly], unicorn: [ux, uy] };
}
/** the balcony: a white balustrade on brackets in front of the door, 1.1 m out from the wall */
function paintBalcony(p, warm) {
  const ef = 4.1, u0 = 3.7, u1 = 7.9, n0 = OSH.es[1] + u0, n1 = OSH.es[1] + u1;
  const [xa] = P(ef, n0, 7), [xb] = P(ef, n1, 7);
  for (let x = Math.round(xa); x <= Math.round(xb); x++) {
    const s = (x - xa) / (xb - xa), n = lerp(n0, n1, s), d = distOf(ef, n);
    const yr = Math.round(rowAt(7.75, d)), yf = Math.round(rowAt(6.95, d)), yb = Math.round(rowAt(6.55, d));
    const post = Math.abs(s - 0.5) < 0.04 || s < 0.04 || s > 0.96, bal = (x % 2 === 0);
    for (let y = yr; y <= yb; y++) {
      let col = null;
      if (y <= yr + 1) col = pick(TRIM, 0.85 + warm * 0.1, x, y);                            // the handrail
      else if (y >= yf) col = pick(TRIM, y === yb ? 0.45 : 0.7, x, y);                         // the floor's edge
      else if (post || bal) col = pick(TRIM, 0.65 + warm * 0.1, x, y);                         // balusters
      if (col) p.set(x, y, col);
    }
    if (s > 0.12 && s < 0.88 && Math.abs((s * 4) % 1 - 0.5) < 0.12) for (let y = yb + 1; y < yb + 4; y++) p.set(x, y, pick(TRIM, 0.5, x, y));   // brackets
  }
}
/** the whole building on its own layer; warm: the floods' warm-up (0 or 1) */
function renderOSH(warm) {
  const p = pix(W, H);
  paintRoof(p);
  paintTower(p, warm);
  paintDormers(p);
  for (let x = 170; x < 310; x++) {
    const hit = hitWall(OSH_POLY, x);
    if (!hit || (hit.i !== 0 && hit.i !== 3)) continue;
    const east = hit.i === 0, uS = east ? hit.len - hit.u : 0, v = east ? 0 : hit.len - hit.u;
    const topH = east ? gableTop(uS) : OSH.eave;
    const y0 = Math.max(0, Math.ceil(rowAt(topH, hit.d))), y1 = Math.floor(rowAt(0, hit.d));
    for (let y = y0; y <= y1; y++) {
      const hh = EYE + hit.d * Math.tan(rowElev(y));
      const col = east ? eastTex(uS, hh, x, y, warm) : northTex(v, hh, x, y, warm);
      if (col) p.set(x, y, col);
    }
  }
  paintBalcony(p, warm);
  const fig = paintFigures(p, warm);
  return { c: p.done(), fig };
}

/* ================= The street: State Street, Devonshire Street, the plaza, the sidewalks ================= */
// Raycast too: each pixel under the horizon is a point of the street. Brick sidewalks with granite curbs, the
// asphalt of the two one-way streets with their lane lines and crosswalks, the plaza's granite in front of the east
// end, the Massacre ring, the Freedom Trail's red line, a steam manhole.
const LAMPS = [{ e: 4.8, n: 1.9, h: 4.2, two: true }, { e: 24, n: -5, h: 4.0 }, { e: 16, n: 15, h: 4.0 }, { e: -15.5, n: 1.0, h: 4.0 }];
const FLOODS = [[4.2, -11.0], [4.2, -7.2], [4.2, -3.4]];  // uplights at the foot of the east wall
const T_GLOW = { e: 1.8, n: -0.6 };                 // the station entrance's light on the sidewalk
// the Freedom Trail: round the Old State House's south-east corner, along the east end to the Massacre ring, across
// State Street at the corner and east along the north sidewalk towards Congress Street (and on to Faneuil Hall)
const TRAIL = [[2.5, -16.5], [5.6, -13.4], [7.4, -9], [9.6, -3.4], [10.2, 0.4], [9.6, 4.4], [9.4, 13.4], [16, 13.9], [30, 14.6], [44, 15.4]];
const MANHOLE = { e: 19, n: 8.6, r: 0.4 };          // steam rising from the district's steam pipes
// the manhole up State Street, a grate on Devonshire, and one in the lane by us (the wind takes that one south-west)
const VENTS = [{ e: 19, n: 8.6, n0: 22, rise: 5.5, we: -1.8, wn: 0 }, { e: 27.5, n: -6.2, n0: 16, rise: 4, we: -1.8, wn: 0 }, { e: 35.4, n: 7.1, n0: 14, rise: 2.6, we: -1.2, wn: -1.9 }];
const CW_STATE = [4.6, 7.8];                        // the crosswalk across State Street at the north-east corner (e range)
const devonS = (e, n) => { const ux = -16 / 18.87, uy = -10 / 18.87; return (e - 41) * ux + (n - 9) * uy; };   // m along Devonshire from the corner
function groundLight(e, n) {
  let s = 0;
  for (const L of LAMPS) s += 0.55 * Math.exp(-((e - L.e) ** 2 + (n - L.n) ** 2) / 26);
  for (const [fe, fn] of FLOODS) s += 0.32 * Math.exp(-((e - fe) ** 2 / 5 + (n - fn) ** 2 / 3));
  s += 0.4 * Math.exp(-((e - T_GLOW.e) ** 2 + (n - T_GLOW.n) ** 2) / 4);
  s += 0.18 * Math.exp(-((n - 30) ** 2) / 30) * (e < 6 && e > -30 ? 1 : 0);                       // 28 State's lit lobby
  s += 0.16 * Math.exp(-((e - 30) ** 2) / 60 - ((n + 9) ** 2) / 12);                              // shops under the Worthington
  return Math.min(1, s);
}
const ASPH = ['#0b0c12', '#101119', '#15161f', '#1b1c26', '#22232e', '#2b2c37', '#363641'];
const BRICKP = ['#140c10', '#1c1014', '#261518', '#331b1c', '#432320', '#562c26', '#6c382c'];
const GRAN = ['#121318', '#191a21', '#21222a', '#2b2c34', '#36373f', '#45454c', '#58575c'];
const WARMG = ['#2a2420', '#3e3226', '#5a4630', '#7a5e3c', '#9c7a4c', '#c09a60'];
function groundTex(e, n, d, x, y) {
  const g = groundLight(e, n), far = clamp((d - 20) / 70, 0, 1), dn = (hash(x, y, 13) - 0.5) * 0.08;
  const [dS] = polyDist(STATE_C, e, n), [dD] = polyDist(DEVON_C, e, n), [dT] = polyDist(TRAIL, e, n);
  const ringR = Math.hypot(e - RING.e, n - RING.n), mh = Math.hypot(e - MANHOLE.e, n - MANHOLE.n);
  const warm = c => g > 0.3 ? mixHex(c, '#c08a50', (g - 0.3) * 0.5) : c;
  if (ringR < RING.r + 0.14) {                                                                // the Massacre ring
    if (ringR > RING.r) { const star = Math.abs(frac(Math.atan2(e - RING.e, n - RING.n) / TAU * 13) - 0.5) < 0.12; return star ? '#f0d890' : pick(GOLD, 0.35 + g * 0.5, x, y); }
    const sett = hash(Math.floor((e - RING.e) / 0.24 + 40), Math.floor((n - RING.n) / 0.24 + 40), 71);
    const joint = frac((e - RING.e) / 0.24) < 0.18 || frac((n - RING.n) / 0.24) < 0.18;
    return warm(pick(GRAN, joint ? 0.12 : 0.32 + sett * 0.3 + g * 0.35, x, y));
  }
  const road = dS < STATE_HW || dD < DEVON_HW;
  if (road) {
    if (mh < MANHOLE.r) return mh > MANHOLE.r - 0.08 ? '#4a4a52' : pick(['#0e0e12', '#1a1a20', '#26262c'], 0.3 + (hash(Math.floor(e * 8), Math.floor(n * 8), 3) < 0.4 ? 0.4 : 0), x, y);
    if (dT < 0.2 && e < 12 && e > 8) return pick(['#3a1012', '#5a1816', '#7a221c'], 0.4 + g * 0.6, x, y);   // the trail painted across
    const inSt = dS < STATE_HW;
    if (inSt && e > 37.2 && e < 37.6) return pick(['#4a4a50', '#6a6a70', '#8a8a8e'], 0.4 + g * 0.5, x, y);   // the stop line
    if (inSt && e > 33.4 && e < 36.6 && frac((n - 3.3) / 1.15) < 0.52) return pick(['#4a4a50', '#6a6a70', '#8a8a8e', '#a8a8aa'], 0.32 + g * 0.6, x, y);   // our crosswalk
    if (inSt && e > CW_STATE[0] && e < CW_STATE[1] && frac((n - 3) / 1.1) < 0.5) return pick(['#4a4a50', '#6a6a70', '#8a8a8e', '#a8a8aa'], 0.35 + g * 0.6 - far * 0.3, x, y);
    const ds = devonS(e, n);
    if (!inSt && ds > 15.5 && ds < 18.5 && frac(polyDist([[41, 9], [9, -15]], e, n)[0] / 1.1 + 0.2) < 0.5) return pick(['#4a4a50', '#6a6a70', '#8a8a8e', '#a8a8aa'], 0.35 + g * 0.6 - far * 0.3, x, y);
    if (inSt && Math.abs(polyDist(STATE_C, e, n)[0]) < 0.08 && frac(e / 6) < 0.45 && e < 36) return pick(['#3a3a40', '#5a5a60', '#7a7a80'], 0.4 + g * 0.5 - far * 0.4, x, y);   // the lane line
    const v = 0.26 + g * 0.55 - far * 0.12 + dn + (vnoise(e / 2, n / 2, 5) - 0.5) * 0.1;
    return warm(pick(ASPH, v, x, y, 0.8));
  }
  if (dS < STATE_HW + 0.24 || dD < DEVON_HW + 0.24) return warm(pick(GRAN, 0.5 + g * 0.45 - far * 0.2, x, y));   // granite curbs
  if (dT < 0.19) return warm(pick(['#2a0c0e', '#3e1214', '#561a18', '#702420', '#8c3026'], 0.3 + g * 0.6 + (frac(dT * 0 + (e + n) / 0.4) < 0.15 ? -0.15 : 0), x, y));   // the trail's red bricks
  // the plaza in front of the east end and round the ring: granite slabs; the sidewalks: brick in running bond
  const plaza = e > 3 && e < 26 && n < 4 && dD > DEVON_HW;
  if (plaza || (n > 16 && e < 30)) {
    const slab = frac(e / 1.2) < 0.06 || frac(n / 0.9) < 0.08;
    return warm(pick(GRAN, (slab ? 0.14 : 0.3) + g * 0.5 - far * 0.15 + dn, x, y, 0.7));
  }
  const row = Math.floor(n / 0.11), joint = frac(n / 0.11) < 0.2 || frac(e / 0.22 + (row % 2) * 0.5) < 0.12;
  return warm(pick(BRICKP, (joint ? 0.15 : 0.32) + g * 0.55 - far * 0.18 + dn, x, y, 0.7));
}
function renderGround(foot) {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) for (let y = Math.max(H0 + 1, Math.floor(foot[x])); y < H; y++) {
    const [e, n, d] = groundAt(x, y);
    if (y < foot[x]) continue;
    p.set(x, y, groundTex(e, n, d, x, y));
  }
  return p.done();
}

/* ================= Street furniture ================= */
// a black lamp post with a lantern (two on the one by the Old State House's corner); drawn at its distance. The post
// runs up into a tapered collar under the lantern (or the arms of the double one), so no lantern floats.
function renderLamp(L) {
  const p = pix(W, H), d = distOf(L.e, L.n), s = pxm(d), [x] = P(L.e, L.n), yb = rowAt(0, d), yt = rowAt(L.h, d);
  const K1 = '#0c0d12', K2 = '#262a34', w = Math.max(1, Math.round(0.14 * s));
  const lw = Math.max(2, Math.round(0.36 * s)), lh = Math.max(3, Math.round(0.62 * s)), ly = Math.round(yt);
  for (let y = ly + lh; y <= Math.round(yb); y++) for (let i = 0; i < w; i++) p.set(x + i, y, i === 0 && w > 1 ? K2 : K1);
  for (let y = Math.round(yb - 0.5 * s); y <= Math.round(yb); y++) for (let i = -1; i <= w; i++) p.set(x + i, y, K1);   // the base
  const heads = L.two ? [-0.45, 0.45] : [0];
  for (const off of heads) {
    const hx = x + Math.round(off * s), l0 = hx - (lw >> 1);
    if (L.two) for (let i = Math.min(x, hx); i <= Math.max(x, hx); i++) p.set(i, ly + lh + 1, K1);   // the arms
    for (let j = 0; j < lh; j++) for (let i = 0; i < lw; i++) {
      const edge = i === 0 || i === lw - 1 || j === 0 || j === lh - 1;
      p.set(l0 + i, ly + j, edge ? K1 : j < lh * 0.35 ? '#fff6dc' : j < lh * 0.7 ? '#ffe2a8' : '#f0c070');
    }
    for (let i = lw > 3 ? 1 : 0; i < (lw > 3 ? lw - 1 : lw); i++) p.set(l0 + i, ly + lh, K1);   // the collar under the lantern
    for (let i = -1; i <= lw; i++) p.set(l0 + i, ly - 1, K1);                                  // the cap
    p.set(hx, ly - 2, K1);
  }
  return { ...trim(p), d, heads: heads.map(off => [x + Math.round(off * s), Math.round(yt + 0.3 * s)]), s };
}
// round planters with clipped evergreens, along the Old State House's east end and north side
const PLANTERS = [{ e: 8.2, n: -13.2 }, { e: 1.4, n: 2.7 }, { e: -9.5, n: 2.3 }];
function renderPlanter(Pl) {
  const p = pix(W, H), d = distOf(Pl.e, Pl.n), s = pxm(d), [x] = P(Pl.e, Pl.n), yb = rowAt(0, d);
  const pw = Math.round(1.1 * s), ph = Math.round(0.7 * s), th = Math.round(1.9 * s);
  for (let j = 0; j < ph; j++) for (let i = 0; i < pw; i++) p.set(x - (pw >> 1) + i, Math.round(yb) - j, pick(['#0c0c10', '#16161c', '#24242c'], 0.3 + (i < pw * 0.3 ? 0.4 : 0) + (j === ph - 1 ? 0.3 : 0), i, j));
  for (let j = 0; j < th; j++) {
    const q = j / th, hw = Math.max(1, Math.round((1 - q) * 0.5 * s));
    for (let i = -hw; i <= hw; i++) p.set(x + i, Math.round(yb) - ph - j, pick(['#06100a', '#0c1a10', '#142616', '#1e341e'], 0.35 + (i < 0 ? 0.25 : -0.1) + (hash(i, j, 81) - 0.5) * 0.5 + (1 - q) * 0.1, i, j));
  }
  return { ...trim(p), d };
}
// a solar-powered trash compactor on our corner: a dark green box with its panel on the lid
const BIN = { e: 28.5, n: 17.2 };
function renderBin() {
  const p = pix(W, H), d = distOf(BIN.e, BIN.n), s = pxm(d), [x] = P(BIN.e, BIN.n), yb = Math.round(rowAt(0, d));
  const bw = Math.round(0.62 * s), bh = Math.round(1.2 * s);
  for (let j = 0; j < bh; j++) for (let i = 0; i < bw; i++) {
    const top = j < Math.round(0.22 * s), col = top ? (j < 2 ? '#3a5a8a' : '#1a2a44') : pick(['#0a1610', '#12241a', '#1c3426', '#2a4a36'], 0.35 + (i < bw * 0.35 ? 0.3 : 0) - j / bh * 0.15, i, j);
    p.set(x - (bw >> 1) + i, yb - bh + j, col);
  }
  for (let i = 2; i < bw - 2; i++) p.set(x - (bw >> 1) + i, yb - bh + Math.round(0.45 * s), '#0a0e0c');   // the hopper's handle
  return { ...trim(p), d };
}
// the traffic signal on our corner: a pole, a short mast arm over State Street with two signal heads facing us, and the
// green street-name signs; the heads cycle green, amber, red every 40 s
const POLE = { e: 30, n: 16 };
const SIGNAL = t => { const c = t % 40; return c < 22 ? 'g' : c < 26 ? 'a' : 'r'; };
// N, M and W drawn wide on the signs: at 1x FONT3's N reads as a Π and its M and W as an H
const WIDE = { N: ['1001', '1101', '1011', '1001', '1001'], M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'] };
const signGlyph = ch => WIDE[ch] ?? FONT3[ch];
const signW = s => [...s].reduce((w, ch) => w + (signGlyph(ch)?.[0].length ?? 3) + 1, 0) - 1;
function sign(c, s, x, y, col) { for (const ch of s) { const g = signGlyph(ch); if (g) glyph(c, g, x, y, col); x += (g?.[0].length ?? 3) + 1; } }
function renderPole() {
  const p = pix(W, H), d = distOf(POLE.e, POLE.n), s = pxm(d), [x] = P(POLE.e, POLE.n), yb = Math.round(rowAt(0, d)), yt = Math.round(rowAt(6.4, d));
  const pw = Math.round(0.2 * s), G1 = '#1a1e1a', G2 = '#2e3430', G3 = '#46504a';
  for (let y = yt; y <= yb; y++) for (let i = 0; i < pw; i++) p.set(x - (pw >> 1) + i, y, i < 2 ? G3 : i < pw * 0.5 ? G2 : G1);
  for (let y = yb - Math.round(0.4 * s); y <= yb; y++) for (let i = -2; i < pw + 2; i++) p.set(x - (pw >> 1) + i, y, i < 1 ? G2 : G1);   // the base
  const [ax] = P(POLE.e, POLE.n - 3.6), ay = yt + 3;                                         // the mast arm, sloping a little
  for (let i = Math.round(ax); i <= x; i++) { const yy = ay + Math.round((x - i) * 0.03); p.set(i, yy, G3); p.set(i, yy + 1, G2); p.set(i, yy + 2, G1); }
  const heads = [];
  for (const k of [0.06, 0.3]) {                                                                // two three-light heads, facing east
    const hx = Math.round(lerp(ax, x, k)), hy = ay + 3, hw = Math.round(0.36 * s), hh = Math.round(1.05 * s);
    for (let j = 0; j < hh; j++) for (let i = 0; i < hw; i++) p.set(hx - (hw >> 1) + i, hy + j, i === 0 || j === 0 ? '#2a2e1e' : '#14160e');
    for (let j = 0; j < 3; j++) heads.push([hx, hy + Math.round((j + 0.5) * hh / 3), Math.max(2, Math.round(hw * 0.32)), j]);
    p.set(hx, hy - 1, G1); p.set(hx, hy - 2, G1);
  }
  // the street-name signs, white on green, bolted under the arm by the pole
  const signs = [['CONGRESS ST', yt + 14], ['DEVONSHIRE ST', yt + 25]];
  const blades = [];
  for (const [txt, by] of signs) {
    const tw = signW(txt) + 4, bx = Math.round(x) - (pw >> 1) - tw;   // whole pixels, or the letters smear
    for (let j = 0; j < 9; j++) for (let i = 0; i < tw; i++) p.set(bx + i, by + j, j === 0 || j === 8 || i === 0 || i === tw - 1 ? '#c8d0c8' : '#0e5a2c');
    blades.push([txt, bx + 2, by + 2]);
    p.set(x - (pw >> 1) - 1, by + 4, G2);
  }
  // the pedestrian signal and its push button
  for (let j = 0; j < Math.round(0.5 * s); j++) for (let i = 0; i < Math.round(0.4 * s); i++) p.set(x + (pw >> 1) + i, yb - Math.round(2.9 * s) + j, i === 0 ? '#3a3a2a' : '#1a1a12');
  return { ...trim(p), d, heads, blades, ped: [x + (pw >> 1) + Math.round(0.2 * s), yb - Math.round(2.65 * s)], yb, s };
}

/* ================= 15-17 State's scaffold and the billboard on it ================= */
// A facade repair: tube-and-clamp scaffolding in front of the north face of 15-17 State, wrapped in dark debris
// netting, two work lamps, and a printed banner across its third and fourth lifts: the city's billboard, a North End
// pastry shop's cannoli. With the billboards off (billboard: false) the scaffold goes too, and the building's windows show.
const BB = { x: 138, y: 127, w: 44, h: 20 };
const SCAF = { e0: -1.2, e1: -17.4, n: -24.9, top: 21 };
function renderScaffold() {
  const p = pix(W, H);
  const [xa] = P(SCAF.e0, SCAF.n), [xb] = P(SCAF.e1, SCAF.n);
  for (let x = Math.round(xa); x <= Math.round(xb); x++) {
    const u = (x - xa) / (xb - xa), e = lerp(SCAF.e0, SCAF.e1, u), d = distOf(e, SCAF.n);
    const yt = Math.round(rowAt(SCAF.top, d)), yb = Math.round(rowAt(0, d));
    const standard = Math.abs(frac((e - SCAF.e0) / -2.1) - 0.0) < 0.06 || Math.abs(frac((e - SCAF.e0) / -2.1) - 1) < 0.06;
    for (let y = yt; y <= yb; y++) {
      const hh = EYE + d * Math.tan(rowElev(y)), lift = frac(hh / 2.0) < 0.07;
      if (standard || lift) p.set(x, y, pick(['#1e2028', '#2e3038', '#44464e'], 0.3 + streetGlow(hh, d) * 1.2, x, y));
      else if (hh > 2.6) p.set(x, y, pick(['#080a10', '#0c0f18', '#121624'], 0.35 + 0.25 * Math.sin(x * 0.7 + hh * 1.3) + streetGlow(hh, d), x, y));   // the netting
    }
    const shed = Math.round(rowAt(3.0, d));                                                    // the sidewalk shed's deck and its lights
    p.set(x, shed, '#4a4c56'); p.set(x, shed + 1, '#2a2c34');
    if (x % 6 === 2) p.set(x, shed + 2, '#ffe8b8');
  }
  return trim(p);
}
// the cannoli: a golden shell, ricotta at both ends with chocolate chips and pistachio, on cream
const CANNOLI = ['.........ww.', '........wwwg', '.......SSwwg', '......SSSS..', '.....SSsSS..', '....SSsSS...', '...SSsSS....',
  '..SSSSS.....', '.wwSSS......', 'wwww........', 'gww.........'];
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#d8ccb0', '#e8dcc0', '#f4ead4'], 0.8 - j * 0.015, i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#7a1a1e'); p.set(i, h - 1, '#7a1a1e'); }
  const key = { w: '#fcf8f0', g: '#5a9a3a', S: '#c88a3c', s: '#8a5424' };
  CANNOLI.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(1 + i, 4 + j, key[row[i]]); });
  p.set(10, 5, '#2a1810'); p.set(2, 14, '#2a1810');                                          // chocolate chips
  const ink = (s, x, y, col) => sign(p.x, s, x, y, col);
  p.done();
  ink("NELLA'S", 15, 2, '#9a1e22');
  ink('CANNOLI', 14, 8, '#3a2418');
  ink('EST 1931', 13, 14, '#2e6a2a');
  return gradeAd(p.c, { level: 0.68, tint: '#141a34', mix: 0.14, wear: 0.15, seed: 31 });
}

/* ================= The loop's timeline ================= */
const TRAINS = [18, 98, 178];                          // a train pulls in below: a rumble, then people come up the stairs
const GUST = { t0: 50, peak: 59, t1: 74 };          // BIG: a gust swirls the leaves across the square
const TOUR = { t0: 88, arrive: 113, leave: 142, t1: 190 };   // BIG: the lantern tour round the corner, at the ring, on its way
const WARM = { t0: 114, full: 124, fall: 148, t1: 162 };     // the floods warm up on the facade while the guide talks
const STRIKES = [206, 208.6, 211.2];               // BIG: the clock strikes (a timed moment, not the hour); pigeons go up
const PIGEONS = { up: 206.4, land: 222, t1: 234 };
const SWEEP = { t0: 158, t1: 214 };                 // the street sweeper crawls up State Street, beacon turning
const JOG = { t0: 24, t1: 40 };
const BIKE = { t0: 126, t1: 136 };
const DOGW = { t0: 214, t1: 236 };
const OFFICE = { t0: 48, t1: 80 };
const PHOTO = [40, 211.4];                          // the couple's flash: their selfie, and again when the clock strikes
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const warmAt = t => smooth(WARM.t0, WARM.full, t) * (1 - smooth(WARM.fall, WARM.t1, t));

/* ================= People ================= */
// One function draws everyone (after rome.js and boston.js): a look gives the proportions for a height and the
// colours dimmed for the night; person() puts it on its feet. Late October: coats, scarves, hats. The floods and the
// lamps catch the shoulders and the hair.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0a0e26', GLOWC = '#f0c890';
const LOOKS = {};
/** a look at height h px; o: long (hair rows), coat (rows of coat over the legs), hat, tri (tricorn), scarf, dress, dk */
function look(id, h, skin, hair, top, bot, o = {}) {
  const key = id + '|' + h;
  if (LOOKS[key]) return LOOKS[key];
  const dk = o.dk ?? 0.34, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return (LOOKS[key] = { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 26 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.32), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: Math.round((o.coat || 0) * h / 20),
    hat: o.hat ? m(o.hat) : null, tri: !!o.tri, scarf: o.scarf ? m(o.scarf) : null, dress: o.dress || 0, hose: o.hose ? m(o.hose) : null });
}
/**
 * One person, feet on row fy, centred on column x; returns { hy, ty, tx, bw } (head top, torso top, torso left, width).
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, cup), 3 out, 4 up and out (pointing), 5 round a
 * neighbour; o.lean: the head towards a neighbour; o.sit: seated; o.up: looking up (the face turned skywards)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, coat = Math.min(P.coat, Math.max(0, legs - 2));
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1, legC = P.dress ? P.top : P.bot;
    ctx.fillStyle = 'rgba(6,8,20,0.38)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    const hose = P.hose;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, hose || legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, hose || legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, hose || legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, legC); R(tx, ly + 2, lw, legs - 2, hose || legC); R(tx + bw - lw, ly + 2, lw, legs - 2, hose || legC);
      if (hose) { R(tx, ly + 2, lw, Math.max(1, (legs >> 2)), legC); R(tx + bw - lw, ly + 2, lw, Math.max(1, legs >> 2), legC); }
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx - (coat ? 1 : 0), ty, bw + (coat ? 2 : 0) - (coat && side ? 1 : 0), th + coat, P.top);  // the torso, a coat's skirt over the legs
  R(side ? (dir > 0 ? tx : tx + bw - 1) : tx + bw - 1, ty + 1, 1, th + coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                                // the light on the shoulders
  if (P.scarf) R(tx + 1, ty, bw - 2, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 1, P.topD); R(ax, ty + th, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD);
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0);
    if (!st) { R(ax, ty + 1, 1, th - 1, P.topD); R(ax, ty + th, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 4, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { for (let k = 0; k < 4; k++) R(x + dir * k, ty + 1 - k, 1, 1, P.topD); R(x + dir * 4, ty - 3, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, o.up ? hr + 1 : hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.tri) { R(hx - 1, hy - 1, hw + 2, 2, '#0e0c10'); R(hx, hy - 2, hw, 1, '#0e0c10'); R(hx - 1, hy - 1, 1, 1, '#3a3036'); R(hx + hw, hy - 1, 1, 1, '#3a3036'); }
  else if (P.hat) R(hx, hy - 1, hw, 2, P.hat);
  return { hy, ty, tx, bw };
}
/** a person standing at world point (e, n): its screen x, feet row and height in px */
function standAt(e, n) {
  const d = distOf(e, n), fy = rowAt(0, d);
  return { x: colOf(e, n), fy, h: Math.max(6, Math.round(fy - rowAt(1.72, d))), d };
}

/* ================= The cast ================= */
// paths in world metres: the tour's route (the Freedom Trail), the north sidewalk, the Old State House's north side
const TOUR_IN = [[-2.2, -16.6], [3.6, -15.6], [6.2, -12.4], [7.8, -6.4], [8.6, -1.4]];
const TOUR_OUT = [[8.6, -1.4], [7.4, 1.2], [6.2, 3.8], [6.2, 13.0], [16, 13.8], [23, 15.8], [29, 19.6]];
const NORTH_SW = [[29, 19.6], [23, 15.8], [16, 13.8], [6, 12.4], [-10, 10.6], [-34, 8.0], [-42, 7.4]];
const OSH_NSW = [[26, 1.8], [18, 1.0], [10, 1.8], [4, 1.6], [-12, -0.5], [-31, -1.6]];
const T_OUT = [[1.6, -0.9], [4.2, 1.6], [6.2, 3.8], [6.2, 13.0], [16, 13.8], [23, 15.8], [29, 19.6]];
const T_WEST = [[1.6, -0.9], [-2, 0.6], [-14, -0.2], [-31, -1.4]];
// the group round the ring, facing the guide (who stands on its far side, his back to the Old State House), placed
// either side of our line of sight to him so that he shows between them; [e, n]
const GATHER = [[14.2, -0.3], [12.9, -2.2], [13.1, -3.4], [12.4, -5.1], [12.1, 2.0], [10.4, 2.2]];
const GUIDE_AT = [8.6, -1.4];
const TOURISTS = [
  ['t0', 0, 0, '#3a4a6a', '#22222c', { coat: 2, scarf: '#a83a2a' }], ['t1', 2, 3, '#7a6a52', '#2a2a32', { long: 3, coat: 3 }],
  ['t2', 1, 1, '#2a4a3a', '#1e2028', { hat: '#c8b8a0' }], ['t3', 3, 0, '#6a2a3a', '#2a2a34', { long: 2, coat: 2, hat: '#2a2a3a' }],
  ['t4', 0, 5, '#4a4a52', '#24242c', { coat: 3, hat: '#5a3a2a' }], ['t5', 1, 4, '#c8a878', '#2a2a32', { long: 4, coat: 3, scarf: '#e8e0d0' }],
];
const T_FOLK = [['p0', 1, 0, '#2a2a34', '#1a1a22', { coat: 3 }], ['p1', 0, 3, '#5a3a4a', '#20202a', { long: 3, coat: 2, scarf: '#d8c8a8' }],
  ['p2', 2, 1, '#3a5a7a', '#1e1e26', { hat: '#1a1a22' }], ['p3', 3, 0, '#7a7a6a', '#2a2a30', { coat: 2 }], ['p4', 1, 2, '#8a3a2a', '#1e2028', { long: 2 }],
  ['p5', 0, 1, '#2e3e2e', '#22222a', { coat: 3, hat: '#6a1a1a' }]];
const lookOf = (spec, h) => look(spec[0], h, spec[1], spec[2], spec[3], spec[4], spec[5]);
const GUIDE = ['guide', 1, 5, '#6a1a1e', '#26202a', { tri: true, coat: 5, hose: '#d8d4c8', dk: 0.26 }];
const COUPLE = [['ca', 2, 0, '#2a3450', '#1e1e28', { coat: 2, scarf: '#b8302a', dk: 0.3 }], ['cb', 0, 4, '#b08a5a', '#262630', { long: 4, coat: 3, hat: '#e8e0d0', dk: 0.3 }]];
const JOGGER = ['jog', 1, 0, '#c8e040', '#14141a', { hat: '#1a1a22', dk: 0.28 }];
const OFFICER = ['off', 0, 2, '#22242c', '#1a1a20', { coat: 4, dk: 0.32 }];
const DOGGER = ['dog', 2, 3, '#5a4a6a', '#20202a', { long: 3, coat: 3, dk: 0.3 }];
const STROLL = [['s0', 3, 0, '#4a3a2a', '#1e1e26', { coat: 2 }], ['s1', 1, 1, '#2a3a5a', '#22222a', { coat: 3, scarf: '#3a6a5a' }]];
const RANK = { tour: 0, couple: 0, rider: 0.4, jog: 0.3, office: 0.42, bike: 0.55, dog: 0.66, stroll0: 0.76, stroll1: 0.88 };
const T_RANKS = [0.15, 0.35, 0.62, 0.82];           // the people up from each train, thinned in turn
const TRAIN_OUT = [[0, 'out', 5.0], [1, 'west', 6.4], [2, 'out', 7.8], [3, 'west', 9.6]];   // who, where to, s after the train
/** walk a polyline at speed (m/s) from time t0: the point, its heading and whether it has arrived */
function walk(pts, t, t0, speed) {
  const L = polyLen(pts), s = (t - t0) * speed;
  if (s <= 0) return { p: pts[0], s: 0, done: false, at: 'start' };
  if (s >= L) return { p: pts[pts.length - 1], s: L, done: true };
  return { p: polyAt(pts, s), s, done: false };
}
// draw a walker at world point q (polyAt result [e, n, heading]); facing: profile by its screen direction, or back
function walker(q, P0, t, o = {}) {
  const [e, n, hd] = q, st = standAt(e, n), P = lookOf(P0, st.h);
  const sx = colOf(e + Math.sin(hd), n + Math.cos(hd)) - st.x, away = Math.abs(sx) < 0.25 * pxm(st.d) * 0.6;
  const view = o.view ?? (away ? (distOf(e + Math.sin(hd), n + Math.cos(hd)) > st.d ? 0 : 1) : 2);
  const stride = step(t + (o.ph || 0), 768) & 1;
  return { st, g: person(st.x, st.fy, P, { view, dir: sx >= 0 ? 1 : -1, step: stride, ...o }), P };
}
// the guide's lantern: a tin lantern with a candle, swinging a little; its warm halo
function lantern(x, y, t, raised) {
  const sw = Math.round(0.7 * wave(t, 120, 0.3));
  R(x + sw, y, 1, 2, '#2a2420');
  R(x - 1 + sw, y + 2, 3, 3, '#ffd890'); dot(x + sw, y + 3, '#fff8e0'); R(x - 1 + sw, y + 5, 3, 1, '#2a2420');
  halo(HL.lantern, x + sw, y + 3, raised ? 1 : 0.8);
}
function drawGuide(t, crowd, list) {
  if (!here(RANK.tour, crowd) || t < TOUR.t0 || t >= TOUR.t1) return;
  let q, talk = false;
  if (t < TOUR.arrive) { const w = walk(TOUR_IN, t, TOUR.t0, polyLen(TOUR_IN) / (TOUR.arrive - TOUR.t0)); q = w.p; }
  else if (t < TOUR.leave) { q = [...GUIDE_AT, Math.PI / 2]; talk = true; }
  else q = walk(TOUR_OUT, t, TOUR.leave, 1.3).p;
  SHAD.push([q[0], q[1]]);
  const st = standAt(q[0], q[1]);
  list.push([st.fy, () => {
    const behind = q[1] < -13.2 && q[0] < 3.5;
    if (behind) { ctx.save(); ctx.beginPath(); ctx.rect(0, 0, Math.floor(P(...OSH.es)[0]), H); ctx.clip(); }
    let raised = false;
    if (talk) {                                                                              // facing the group: raising the lantern, pointing at the balcony
      const c = (t - TOUR.arrive) % 12, point = c > 5 && c < 8.5, P = lookOf(GUIDE, st.h);
      raised = c < 4.5;
      const g = person(st.x, st.fy, P, { view: 1, aR: raised ? 1 : 3, aL: point ? 4 : 0 });
      lantern(raised ? st.x + (g.bw >> 1) + 1 : g.tx + g.bw + 3, raised ? g.hy - 5 : g.ty + 2, t, raised);
    } else {
      const w = walker(q, GUIDE, t, { aR: 3 });
      lantern(w.st.x + (w.g.bw >> 1) + 3, w.g.ty + 2, t, false);
    }
    if (behind) ctx.restore();
  }]);
  // the group: in behind him in a line, gathered round the ring, then after him across State Street
  TOURISTS.forEach((spec, i) => {
    const lag = 1.1 + i * 1.0, side = [0.5, -0.6, 0.2, -0.3, 0.7, -0.7][i];
    let q2, view = null, o = {};
    if (t < TOUR.arrive + lag) {
      const w = walk(TOUR_IN, t, TOUR.t0 + lag, polyLen(TOUR_IN) / (TOUR.arrive - TOUR.t0));
      q2 = w.p;
      if (t > TOUR.arrive - 2 + lag * 0.3) {                                                   // fanning out to their places
        const u = smooth(TOUR.arrive - 2 + lag * 0.3, TOUR.arrive + lag, t);
        q2 = [lerp(q2[0], GATHER[i][0], u), lerp(q2[1], GATHER[i][1], u), Math.PI / 2];
      }
    } else if (t < TOUR.leave + lag * 0.9) {
      q2 = [...GATHER[i], -Math.PI / 2]; view = 0;
      const c = (t + i * 3.7) % 16;
      o = { aR: (i === 1 || i === 4) && c < 6 ? 2 : 0, lean: i === 2 && c > 10 ? -1 : 0 };      // photos of the balcony, a word to a neighbour
      if (i === 3 && warmAt(t) > 0.5 && c < 5) o.aL = 4;
    } else {
      const s0 = TOUR.leave + lag * 0.9, u = smooth(s0, s0 + 2.5, t), w = walk(TOUR_OUT, t, s0 + 1.5, 1.3);
      q2 = t < s0 + 2.5 ? [lerp(GATHER[i][0], TOUR_OUT[0][0], u), lerp(GATHER[i][1], TOUR_OUT[0][1], u), 0] : [w.p[0] + Math.cos(w.p[2]) * side, w.p[1] - Math.sin(w.p[2]) * side, w.p[2]];
      if (w.done) return;
    }
    SHAD.push([q2[0], q2[1]]);
    const st2 = standAt(q2[0], q2[1]);
    list.push([st2.fy + i * 0.01, () => {
      const behind = q2[1] < -13.2 && q2[0] < 3.5;
      if (behind) { ctx.save(); ctx.beginPath(); ctx.rect(0, 0, Math.floor(P(...OSH.es)[0]), H); ctx.clip(); }
      if (view === 0) person(st2.x, st2.fy, lookOf(spec, st2.h), { view: 0, ...o });
      else walker(q2, spec, t, { ph: i * 0.37 });
      if (behind) ctx.restore();
    }]);
  });
}
// a delivery rider waiting on the corner by his e-bike for the next order, phone in hand, looking up now and then
const RIDER = ["rider", 3, 0, "#2a6a8a", "#1a1a22", { hat: "#1a1a20", dk: 0.3 }], RIDER_AT = [20.2, 16.8];
function drawRider(t, crowd, list) {
  if (!here(RANK.rider, crowd)) return;
  const st = standAt(...RIDER_AT), s = pxm(st.d);
  list.push([st.fy, () => {
    const x = Math.round(st.x), fy = Math.round(st.fy), wr = Math.round(0.33 * s), bx = x + Math.round(0.9 * s);
    for (const cx of [bx - Math.round(0.55 * s), bx + Math.round(0.55 * s)]) {                 // the bike beside him, side on
      ctx.fillStyle = "#14141a"; ctx.fillRect(cx - wr, fy - 1, wr * 2 + 1, 1); ctx.fillRect(cx - wr, fy - wr * 2, 1, wr * 2); ctx.fillRect(cx + wr, fy - wr * 2, 1, wr * 2); ctx.fillRect(cx - wr, fy - wr * 2, wr * 2 + 1, 1);
    }
    R(bx - Math.round(0.55 * s), fy - wr - 1, Math.round(1.1 * s), 1, "#3a3a44"); R(bx + Math.round(0.4 * s), fy - Math.round(1.0 * s), 1, Math.round(0.6 * s), "#3a3a44");
    R(bx - Math.round(0.75 * s), fy - Math.round(1.25 * s), Math.round(0.5 * s), Math.round(0.45 * s), "#1e6a8a");   // the delivery box
    const c = t % 30, up = c > 20 && c < 24 || STRIKES.some(k => inWin(t, k, k + 5)), g = person(x, fy, lookOf(RIDER, st.h), { view: up ? 0 : 1, aR: up ? 0 : 2, aL: up ? 0 : 2 });
    if (!up) { R(x - 1, g.ty + 1, 3, 2, "#9ad0ff"); halo(HL.tiny, x, g.ty + 1, 0.5); }
  }]);
}
const COUPLE_AT = [[16.6, 13.2], [17.4, 13.7]];
function drawCouple(t, crowd, list) {
  if (!here(RANK.couple, crowd)) return;
  const [a, b] = COUPLE_AT.map(([e, n]) => standAt(e, n));
  const selfie = PHOTO.some(p => inWin(t, p - 5, p + 2.5)), look = (t % 48) > 30 && !selfie, up = STRIKES.some(s => inWin(t, s - 1, s + 8));
  list.push([a.fy, () => {
    const PA = lookOf(COUPLE[0], a.h), PB = lookOf(COUPLE[1], b.h);
    if (selfie) {                                                                              // side by side, facing us, his arm up with the phone
      person(b.x, b.fy, PB, { view: 1, lean: -1 });
      const g = person(a.x, a.fy, PA, { view: 1, aR: 1, lean: 1 });
      R(g.tx + g.bw, g.hy - 4, 2, 2, '#1a1c24');
      const flash = PHOTO.find(p => inWin(t, p, p + 0.25));
      if (flash !== undefined) halo(HL.flash, g.tx + g.bw + 1, g.hy - 3, 1);
    } else {                                                                                   // looking at the Old State House, pointing at the tower
      person(b.x, b.fy, PB, { view: 0, aL: look || up ? 4 : 0, lean: (t % 40) < 6 ? -1 : 0 });
      person(a.x, a.fy, PA, { view: 0, aR: (t % 24) < 5 ? 2 : 5 });
    }
  }]);
}
function drawTrainFolk(t, crowd, list) {
  TRAINS.forEach((t0, k) => {
    TRAIN_OUT.forEach(([who, where, dt], j) => {
      if (!here(T_RANKS[j], crowd)) return;
      const ts = t0 + dt, path = where === 'out' ? T_OUT : T_WEST;
      if (t < ts || t >= ts + 60) return;
      const w = walk(path, t, ts, 1.35);
      if (w.done) return;
      const spec = T_FOLK[(who + k * 2) % T_FOLK.length], st = standAt(w.p[0], w.p[1]);
      list.push([st.fy, () => walker(w.p, spec, t, { ph: j * 0.3, aR: j === 2 ? 2 : 0 })]);
    });
  });
}
// the rest of the evening's people: walkers who pass once a loop each (thinned by rank), two friends talking by the
// station entrance, a man on the island waiting for the light with a coffee
const PLAZA_X = [[25.5, 3.0], [18, 0.8], [10.5, 0.4], [4.2, 1.6], [1.6, -0.9]];
const DEVON_SW = [[38, -1.5], [31, -6.5], [24, -12], [18, -17], [13, -21]];
const AMB = [
  { path: [...NORTH_SW].reverse(), t0: 6, v: 1.3, rank: 0.2, spec: ['a0', 0, 2, '#4a2a2a', '#1e1e26', { coat: 3, long: 3 }] },
  { path: PLAZA_X, t0: 52, v: 1.25, rank: 0.46, spec: ['a1', 3, 0, '#2a3a2a', '#1a1a22', { coat: 2, hat: '#3a2a1e' }] },
  { path: DEVON_SW, t0: 70, v: 1.35, rank: 0.55, spec: ['a2', 1, 4, '#6a5a7a', '#22222a', { long: 2, coat: 2 }] },
  { path: [...OSH_NSW].reverse(), t0: 112, v: 1.2, rank: 0.64, spec: ['a3', 2, 0, '#3a4a5a', '#1e1e26', { scarf: '#c8a040' }] },
  { path: NORTH_SW, t0: 146, v: 1.3, rank: 0.72, spec: ['a4', 0, 1, '#5a4a3a', '#24242c', { coat: 3 }] },
  { path: [...DEVON_SW].reverse(), t0: 194, v: 1.3, rank: 0.8, spec: ['a5', 1, 5, '#2a2a3a', '#1a1a20', { coat: 4, hat: '#1a1a20' }] },
  { path: [...PLAZA_X].reverse(), t0: 222, v: 1.25, rank: 0.9, spec: ['a6', 3, 3, '#7a3a4a', '#20202a', { long: 3 }] },
  { path: OSH_NSW, t0: 28, v: 1.25, rank: 0.97, spec: ['a7', 2, 1, '#3a3a2a', '#1e1e24', { hat: '#5a1a1a' }] },
];
const FRIENDS = [['f0', 0, 0, '#3a2a4a', '#1e1e28', { coat: 2, long: 3 }], ['f1', 2, 1, '#4a5a3a', '#1e1e26', { hat: '#2a2a32' }]], FRIENDS_AT = [[4.6, 0.9], [5.4, 1.7]];
const WAITER = ['w0', 1, 0, '#5a5a62', '#1e1e26', { coat: 3, scarf: '#8a2a2a' }], WAITER_AT = [24.8, 3.0];
function drawExtras(t, crowd, list) {
  AMB.forEach((A, i) => {
    if (!here(A.rank, crowd)) return;
    const L = polyLen(A.path), tt = ((t - A.t0) % LOOP + LOOP) % LOOP;
    if (tt * A.v >= L) return;
    const q = polyAt(A.path, tt * A.v), st = standAt(q[0], q[1]);
    if (st.fy > 216) return;
    if (q[0] > 3.4 && q[0] < 16 && q[1] > -12 && q[1] < 1.5) SHAD.push([q[0], q[1]]);
    list.push([st.fy, () => walker(q, A.spec, t, { ph: i * 0.29, aR: i % 3 === 1 ? 2 : 0 })]);
  });
  if (here(0.58, crowd)) FRIENDS_AT.forEach(([e, n], i) => {                                  // talking, a hand out now and then
    const st = standAt(e, n), c = (t + i * 7) % 20;
    list.push([st.fy, () => person(st.x, st.fy, lookOf(FRIENDS[i], st.h), { view: 2, dir: i ? -1 : 1, aR: c < 3 ? 3 : 0, lean: c > 14 ? (i ? -1 : 1) : 0 })]);
  });
  if (here(0.68, crowd)) {
    const st = standAt(...WAITER_AT), c = t % 40, sip = c > 8 && c < 11;
    list.push([st.fy, () => { const g = person(st.x, st.fy, lookOf(WAITER, st.h), { view: c > 20 && c < 30 ? 0 : 1, aR: sip ? 2 : 3 }); R(g.tx + g.bw + (sip ? -1 : 2), sip ? g.hy + 3 : g.ty + 1, 2, 2, '#e8e0d0'); }]);
  }
}
function drawWalkers(t, crowd, list) {
  if (here(RANK.jog, crowd) && inWin(t, JOG.t0, JOG.t1)) {                                    // a jogger, out along the north sidewalk
    const w = walk(NORTH_SW, t, JOG.t0, polyLen(NORTH_SW) / (JOG.t1 - JOG.t0));
    if (!w.done) { const st = standAt(w.p[0], w.p[1]); list.push([st.fy, () => walker(w.p, JOGGER, t * 1.6, { aR: 4 })]); }
  }
  if (here(RANK.office, crowd) && inWin(t, OFFICE.t0, OFFICE.t1)) {                           // a late office worker, home by the T
    const path = [[29, 19.6], [23, 15.8], [16, 13.8], [6.2, 13.0], [6.2, 3.8], [4.2, 1.6], [1.6, -0.9]];
    const w = walk(path, t, OFFICE.t0, 1.3);
    if (!w.done) {
      const st = standAt(w.p[0], w.p[1]), fade = w.s > polyLen(path) - 1.2;
      if (!fade) list.push([st.fy, () => { const g = walker(w.p, OFFICER, t, { aR: 3 }); R(g.st.x + 2, g.g.ty + 3, 2, 2, '#2a1e16'); }]);
    }
  }
  if (here(RANK.dog, crowd) && inWin(t, DOGW.t0, DOGW.t1)) {                                  // a dog walker across the plaza and away
    const w = walk(OSH_NSW, t, DOGW.t0, polyLen(OSH_NSW) / (DOGW.t1 - DOGW.t0));
    if (!w.done) {
      const st = standAt(w.p[0], w.p[1]);
      list.push([st.fy, () => {
        const g = walker(w.p, DOGGER, t, { aR: 3 }), s = pxm(st.d);
        const dx = st.x + (colOf(w.p[0] + Math.sin(w.p[2]) * 1.6, w.p[1] + Math.cos(w.p[2]) * 1.6) - st.x), dl = Math.max(3, Math.round(0.7 * s)), dh = Math.max(2, Math.round(0.45 * s));
        R(dx - (dl >> 1), st.fy - dh, dl, Math.max(1, dh - 1), '#3a2a1e'); R(dx + (dl >> 1) - 1, st.fy - dh - 1, 2, 2, '#3a2a1e');
        if (step(t, 960) & 1) R(dx - (dl >> 1), st.fy - 1, 1, 1, '#3a2a1e'); else R(dx + (dl >> 1) - 1, st.fy - 1, 1, 1, '#3a2a1e');
        ctx.globalAlpha = 0.7; for (let k = 0; k < 4; k++) dot(lerp(g.st.x + 3, dx, k / 4), lerp(g.g.ty + 3, st.fy - dh, k / 4), '#c8323a'); ctx.globalAlpha = 1;

      }]);
    }
  }
  STROLL.forEach((spec, i) => {                                                                // two people strolling the north side, each once a loop
    if (!here(i ? RANK.stroll1 : RANK.stroll0, crowd)) return;
    const t0 = i ? 168 : 96, path = i ? NORTH_SW : [...NORTH_SW].reverse(), dur = polyLen(path) / 1.25;
    if (!inWin(t, t0, t0 + dur)) return;
    const w = walk(path, t, t0, 1.25);
    if (w.done) return;
    const st = standAt(w.p[0], w.p[1]);
    if (st.fy > 214) return;
    list.push([st.fy, () => walker(w.p, spec, t, { ph: i * 0.5, aR: i ? 2 : 0 })]);
  });
}

/* ================= Traffic: State Street and Devonshire Street, both one way, away from us ================= */
// Cars set off from the corner behind us on the green and drive away up State Street (along the Old State House's north
// side, then out of sight behind the Ames Building into Court Street) or down Devonshire Street; seen from behind and a
// little from their right: the rear, the right flank, the roof, tail lights, and their headlights' pool on the road ahead.
const CARS = [];                                    // { t0, road, lane, col, kind, v }
{
  const r = rng(2026), cols = ['#2a2e3a', '#6a6e78', '#1a1a20', '#8a1e22', '#c8c8cc', '#2a3e5a', '#3a3a30', '#e8e8e8'];
  for (let cyc = 0; cyc < 6; cyc++) {
    const base = cyc * 40;
    // State Street on its green; Devonshire Street from Congress Street on its green (State Street's red) and from State on its own
    for (const [off, road] of [[1.4, 'S'], [5.8, 'S'], [9.6, 'D'], [12.2, 'S'], [16.8, 'S'], [3.2, 'D'], [27.5, 'D'], [31.4, 'D'], [35.6, 'D']]) {
      if (r() < (road === 'D' ? 0.3 : 0.18)) continue;
      const kind = (cyc === 1 && off === 5.8) || (cyc === 4 && off === 12.2) ? 'taxi' : 'car';
      CARS.push({ t0: base + off + r() * 0.8, road, lane: r() < 0.5 ? -1 : 1, col: kind === 'taxi' ? '#e8e4d8' : cols[Math.floor(r() * cols.length)], kind, v: 8 + r() * 3 });
    }
  }
}
const ROADS = { S: STATE_C.slice(1), D: DEVON_C };
const ROAD_END = { S: 78, D: 50 };                  // where they pass out of sight (behind the Ames; round Devonshire's bend)
/** the screen polygon of a box face given 4 world corners [e, n, h]; filled column by column (convex) */
function fillQuad(pts, col) {
  const sp = pts.map(([e, n, h]) => P(e, n, h));
  let x0 = Infinity, x1 = -Infinity;
  for (const [x] of sp) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); }
  ctx.fillStyle = col;
  for (let x = Math.round(x0); x <= Math.round(x1); x++) {
    let ya = Infinity, yb = -Infinity;
    for (let i = 0; i < 4; i++) {
      const [ax, ay] = sp[i], [bx, by] = sp[(i + 1) % 4];
      if ((x < Math.min(ax, bx) - 0.5) || (x > Math.max(ax, bx) + 0.5)) continue;
      const u = Math.abs(bx - ax) < 1e-6 ? 0.5 : clamp((x - ax) / (bx - ax), 0, 1), y = lerp(ay, by, u);
      ya = Math.min(ya, y, Math.abs(bx - ax) < 1e-6 ? Math.min(ay, by) : y); yb = Math.max(yb, y, Math.abs(bx - ax) < 1e-6 ? Math.max(ay, by) : y);
    }
    if (yb >= ya) ctx.fillRect(x, Math.round(ya), 1, Math.max(1, Math.round(yb) - Math.round(ya) + 1));
  }
  return sp;
}
/** a vehicle with its rear centre at (e, n) heading hd (a bearing in radians): w, len, body and cabin heights */
function drawVehicle(e, n, hd, V, t, a = 1) {
  const fx = Math.sin(hd), fy = Math.cos(hd), rx = fy, ry = -fx, { w, len, hb, hc } = V;
  const c = (s, q, h) => [e + rx * s * w / 2 + fx * q * len, n + ry * s * w / 2 + fy * q * len, h];
  const d = distOf(e, n), s = pxm(d), seeRight = (CAM[0] - e) * rx + (CAM[1] - n) * ry > 0, sd = seeRight ? 1 : -1;
  ctx.globalAlpha = a;
  // its shadow on the road, then the flank, the rear, the cabin's glass and the roof
  fillQuad([c(-1.1, -0.05, 0), c(1.1, -0.05, 0), c(1.1, 1.05, 0), c(-1.1, 1.05, 0)], 'rgba(4,5,10,0.5)');
  const bodyD = mixHex(V.col, '#05060c', 0.55), bodyM = mixHex(V.col, '#0a0c18', 0.3), glass = '#0e121c';
  fillQuad([c(sd, 0, 0.28), c(sd, 1, 0.28), c(sd, 1, hb), c(sd, 0, hb)], bodyD);
  fillQuad([c(sd * 0.96, V.cab0, hb), c(sd * 0.96, V.cab1, hb), c(sd * 0.9, V.cab1 - 0.06, hc), c(sd * 0.9, V.cab0 + 0.08, hc)], glass);
  fillQuad([c(-1, 0, 0.28), c(1, 0, 0.28), c(1, 0, hb), c(-1, 0, hb)], bodyM);
  fillQuad([c(-0.92, V.cab0, hb), c(0.92, V.cab0, hb), c(0.84, V.cab0 + 0.08, hc), c(-0.84, V.cab0 + 0.08, hc)], glass);
  fillQuad([c(-0.84, V.cab0 + 0.08, hc), c(0.84, V.cab0 + 0.08, hc), c(0.84, V.cab1 - 0.06, hc), c(-0.84, V.cab1 - 0.06, hc)], mixHex(V.col, '#202430', 0.35));
  // wheels on the flank, tyres a shade lighter than the road
  for (const q of [0.17, 0.8]) fillQuad([c(sd * 1.01, q - 0.07, 0), c(sd * 1.01, q + 0.07, 0), c(sd * 1.01, q + 0.07, 0.62), c(sd * 1.01, q - 0.07, 0.62)], '#1c1c22');
  // tail lights and their glow; the number plate
  const tl = P(...c(-0.8, 0, hb - 0.2)), tr = P(...c(0.8, 0, hb - 0.2)), tw = Math.max(1, Math.round(0.28 * s));
  R(tl[0] - (tw >> 1), tl[1], tw, Math.max(1, Math.round(0.12 * s)), '#ff3a30'); R(tr[0] - (tw >> 1), tr[1], tw, Math.max(1, Math.round(0.12 * s)), '#ff3a30');
  halo(HL.tail, tl[0], tl[1], 0.6 * a); halo(HL.tail, tr[0], tr[1], 0.6 * a);
  const pl = P(...c(0, 0, 0.5)); R(pl[0] - Math.max(1, Math.round(0.25 * s)), pl[1], Math.max(1, Math.round(0.5 * s)), Math.max(1, Math.round(0.13 * s)), '#a8a8a0');
  if (V.kind === 'taxi') { const tp = P(...c(0, 0.45, hc + 0.18)); R(tp[0] - Math.max(1, Math.round(0.35 * s)), tp[1], Math.max(2, Math.round(0.7 * s)), Math.max(1, Math.round(0.18 * s)), '#fff0b8'); halo(HL.tiny, tp[0], tp[1], 0.8 * a); }
  // the headlights' pool on the road ahead
  const hp = P(e + fx * (len + 7), n + fy * (len + 7), 0);
  halo(HL.beam, hp[0], hp[1], 0.6 * a * clamp(40 / d, 0.4, 1.2));
  headlightWash(e + fx * len, n + fy * len, hd, a);
  const bp = P(e - fx * 1.2, n - fy * 1.2, 0); halo(HL.trail, bp[0], bp[1], 0.6 * a * clamp(30 / d, 0.3, 1.2));   // the tail lights' red on the road
  ctx.globalAlpha = 1;
}
const CAR_V = { w: 1.8, len: 4.5, hb: 0.95, hc: 1.42, cab0: 0.22, cab1: 0.78 };
/** the first wall a car's headlights meet ahead of it: [e, n, distance] or null */
function wallAhead(e, n, hd) {
  const dx = Math.sin(hd), dy = Math.cos(hd);
  let best = null;
  for (const B of BLDGS) for (let i = 0; i < B.poly.length; i++) {
    const [ax, ay] = B.poly[i], [bx2, by2] = B.poly[(i + 1) % B.poly.length], ex = bx2 - ax, ey = by2 - ay, det = ex * dy - dx * ey;
    if (Math.abs(det) < 1e-9) continue;
    const qx = ax - e, qy = ay - n, d = (ex * qy - ey * qx) / det, u = (dx * qy - dy * qx) / det;
    if (d > 2 && u >= 0 && u <= 1 && (!best || d < best[2])) best = [e + dx * d, n + dy * d, d];
  }
  return best;
}
function headlightWash(e, n, hd, a) {
  const w = wallAhead(e, n, hd);
  if (!w || w[2] > 70) return;
  const [x, y] = P(w[0] - Math.sin(hd) * 0.3, w[1] - Math.cos(hd) * 0.3, 1.6), near = distOf(w[0], w[1]) < 55;
  halo(near ? HL.wash : HL.washF, x, y, a * clamp(22 / w[2], 0.15, 1));
}
function drawCars(t, list) {
  for (const C0 of CARS) {
    for (const dtl of [0, LOOP]) {                                                          // a car that set off near the wrap
      const tt = t + dtl - C0.t0;
      if (tt < 0) continue;
      const s = tt * C0.v;
      if (s > ROAD_END[C0.road] + 6) continue;
      const [e0, n0, hd] = polyAt(ROADS[C0.road], s), off = C0.lane * (C0.road === 'S' ? 1.9 : 1.7);
      const e = e0 + Math.cos(hd) * off, n = n0 - Math.sin(hd) * off, d = distOf(e, n);
      if (d < 4.5) continue;
      const a = 1 - smooth(ROAD_END[C0.road], ROAD_END[C0.road] + 6, s);
      list.push([rowAt(0, d), () => drawVehicle(e, n, hd, { ...CAR_V, col: C0.col, kind: C0.kind }, t, a)]);
    }
  }
}
// the street sweeper: a squat white truck with its amber beacon turning, brushes spinning, a fine mist behind
const SWEEP_V = { w: 2.3, len: 5.2, hb: 1.6, hc: 2.5, cab0: 0.62, cab1: 0.98, col: '#d8d8d0', kind: 'sweeper' };
function drawSweeper(t, list) {
  if (!inWin(t, SWEEP.t0, SWEEP.t1)) return;
  const s = (t - SWEEP.t0) * 1.6 + 2, [e0, n0, hd] = polyAt(ROADS.S, s), e = e0 + Math.cos(hd) * 1.9, n = n0 - Math.sin(hd) * 1.9, d = distOf(e, n);
  if (d < 5) return;
  const a = 1 - smooth(ROAD_END.S - 4, ROAD_END.S + 2, s);
  list.push([rowAt(0, d), () => {
    drawVehicle(e, n, hd, SWEEP_V, t, a);
    const top = P(e + Math.sin(hd) * 3.6, n + Math.cos(hd) * 3.6, 2.75), b = 0.5 + 0.5 * Math.cos(TAU * t / 1.5);
    ctx.globalAlpha = a; R(top[0], top[1], 2, 2, b > 0.5 ? '#ffb030' : '#8a5a1a'); ctx.globalAlpha = 1;
    halo(HL.amber, top[0], top[1], (0.35 + 0.65 * b) * a);
    const ang = TAU * t / 1.5;                                                                // its turning light on the walls round it
    for (const [we, wn, wh, ph] of [[-10, -1.8, 5, 0], [-46, 14, 6, 0.3], [6, 30, 5, 0.55], [-2, -26, 6, 0.8]]) {
      const f = Math.max(0, Math.cos(ang - TAU * ph)), [wx2, wy2] = P(we, wn, wh);
      if (f > 0.02) halo(HL.amberWash, wx2, wy2, f * f * a * clamp(30 / distOf(we - e + CAM[0], wn - n + CAM[1]), 0.3, 1));
    }
    for (let k = 0; k < 10; k++) {                                                            // the mist and the brushes' spray
      const { u } = cycle(t, 480, k / 10), q = P(e - Math.sin(hd) * u * 3 + Math.cos(hd) * (hash(k, 2) - 0.5) * 2, n - Math.cos(hd) * u * 3, 0.3 + u * 0.8);
      ctx.globalAlpha = 0.25 * (1 - u) * a; dot(q[0], q[1], '#c8d0e0'); ctx.globalAlpha = 1;
    }
  }]);
}
// a cyclist riding away up State Street: a dark rider over a thin wheel, his red light blinking
const BIKER = ['bk', 1, 0, '#2a5a7a', '#14141a', { hat: '#d8d8d0' }];
function drawBike(t, crowd, list) {
  if (!here(RANK.bike, crowd) || !inWin(t, BIKE.t0, BIKE.t1)) return;
  const s = (t - BIKE.t0) * 6.5 + 3, [e0, n0, hd] = polyAt(ROADS.S, s), e = e0 + Math.cos(hd) * 3.0, n = n0 - Math.sin(hd) * 3.0;
  const st = standAt(e, n);
  if (st.h > 70) return;
  list.push([st.fy, () => {
    const P0 = lookOf(BIKER, Math.round(st.h * 0.82)), x = Math.round(st.x), fy = Math.round(st.fy);
    const wh = Math.max(2, Math.round(st.h * 0.38));
    R(x, fy - wh, 1, wh, '#16161c'); R(x - 1, fy - wh - 1, 3, 1, '#2a2a30');
    R(x - (P0.tw >> 1), fy - wh - P0.th - 1, P0.tw, P0.th, P0.top); R(x - (P0.tw >> 1), fy - wh - P0.th - 1, P0.tw, 1, P0.topL);
    R(x - 1, fy - wh - P0.th - 1 - P0.hh, P0.hw, P0.hh, P0.hair); R(x - 1, fy - wh - P0.th - 2 - P0.hh, P0.hw, 2, P0.hat);
    R(x - (P0.tw >> 1), fy - wh + 1, 1, wh - 2, P0.bot); R(x + (P0.tw >> 1) - 1, fy - wh + 1 + (step(t, 960) & 1), 1, wh - 3, P0.bot);
    if (frac(t * 2) < 0.5) { dot(x, fy - wh + 1, '#ff3a30'); halo(HL.tail, x, fy - wh + 1, 0.8); }
  }]);
}

/* ================= Dry leaves: falling on the wind at three depths, skittering along the street in gusts ================= */
// Honey locust gold, plane-tree browns, a few maple reds, blown in from the trees up Congress Street on an east wind.
const LEAF_PALS = [['#c89a2a', '#7a5a14'], ['#9a6a2a', '#5a3a14'], ['#b8742a', '#6a3e14'], ['#a83a1e', '#5a1a0e'], ['#d8b040', '#8a6a1a']];
const LEAF_SHAPES = [['11'], ['11', '1.'], ['.11', '11.'], ['..11', '.111', '111.', '1...']];
let LEAF_SPR;                                       // [palette][shape][flip]
const FALL = [
  { count: 30, nMin: 6, nMax: 9, y0: [10, 110], land: [186, 194], dx: -150, shape: 1 },        // far: across the canyon, down by One Boston Place
  { count: 18, nMin: 8, nMax: 11, y0: [40, 150], land: [194, 206], dx: -190, shape: 2 },       // mid: over the plaza
  { count: 8, nMin: 10, nMax: 13, y0: [60, 170], land: [H + 8, H + 8], dx: -240, shape: 3 },   // near: past us
];
FALL.forEach((L, li) => {
  const r = rng(740 + li);
  L.leaves = Array.from({ length: L.count }, (_, i) => ({ id: i + li * 100, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), ks: 30 + Math.floor(r() * 30), p2: r(), pal: Math.floor(r() * 5) }));
});
const gustAt = t => env(t, GUST.t0, GUST.t1, 6) + 0.25 * smooth(0.6, 1, 0.5 + 0.5 * wave(t, 30, 0.1));   // the big gust, and the small ones
function drawFalling(L, depth, t) {
  for (const l of L.leaves) {
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2), g = env(t, GUST.t0, GUST.t1, 6);
    const x0 = 120 + hash(l.id, cyc, 5) * 420, y0 = lerp(L.y0[0], L.y0[1], hash(l.id, cyc, 6)), yEnd = lerp(L.land[0], L.land[1], hash(l.id, cyc, 7));
    const x = x0 + u * L.dx * (1 + g * 0.8) + sw * (3 + depth * 3), y = y0 + u * (yEnd - y0) - Math.abs(sw) * (depth + 1) - g * 20 * Math.sin(Math.PI * u);
    if (x < -6 || x > W + 6) continue;
    const spr = LEAF_SPR[(l.pal + cyc) % 5][L.shape][wave(t, l.ks * 2, l.p2) > 0 ? 1 : 0];
    ctx.globalAlpha = L.land[0] < H && u > 0.92 ? (1 - u) * 12 : 1;
    ctx.drawImage(spr, Math.round(x), Math.round(y));
  }
  ctx.globalAlpha = 1;
}
const SKITTER = [];                                 // fallen leaves the gusts hop west along the ground: 2 m a gust, 30 gusts a loop
{
  const r = rng(752);
  for (let i = 0; i < 70; i++) SKITTER.push({ e: -22 + r() * 60, n: -14 + r() * 32, ph: r(), pal: Math.floor(r() * 5) });
  for (let i = 0; i < 26; i++) SKITTER.push({ e: -22 + r() * 60, n: 8 + r() * 10, ph: r(), pal: Math.floor(r() * 5) });   // more along State Street, by us
}
let FOOT;                                           // the row where the ground starts in each column
function drawSkitter(t) {
  for (const l of SKITTER) {
    const { cyc, u } = cycle(t, 30, l.ph), big = env(t, GUST.t0, GUST.t1, 4), mv = smooth(0, 0.2, u);
    const e = -22 + ((((l.e + 22) - 2 * (cyc + mv)) % 60) + 60) % 60, n = l.n + lerp(Math.sin(cyc * 1.7 + l.ph * 9), Math.sin(((cyc + 1) % 30) * 1.7 + l.ph * 9), mv) * 0.6;
    const d = distOf(e, n), [x, y] = P(e, n, 0), xi = Math.round(x);
    if (xi < 0 || xi >= W || y < FOOT[xi] + 0.5 || d < 3) continue;
    const hop = u < 0.2 ? Math.sin(Math.PI * u / 0.2) * (0.12 + big * 0.5) : 0, sh = d < 12 ? 2 : d < 30 ? 1 : 0;
    const spr = LEAF_SPR[l.pal][sh][u < 0.2 && step(t, 1920) & 1 ? 1 : 0];
    ctx.drawImage(spr, xi, Math.round(y - hop * pxm(d) - spr.height + 1));
  }
}
// the big gust's swirl: leaves lifted off the plaza in a turning column that runs across to the Old State House's corner
const SWIRL = Array.from({ length: 40 }, (_, i) => ({ ph: hash(i, 1, 91), r: 0.5 + hash(i, 2, 91) * 2.2, hmax: 1.5 + hash(i, 3, 91) * 6, pal: i % 5, sp: 0.8 + hash(i, 4, 91) * 0.6 }));
function drawSwirl(t, list) {
  if (t < GUST.t0 + 2 || t >= GUST.t1 + 2) return;
  const u = (t - GUST.t0 - 2) / (GUST.t1 - GUST.t0), ce = lerp(25, 4, u), cn = lerp(2.5, 3.5, u), lift = Math.sin(Math.PI * clamp(u * 1.15, 0, 1));
  list.push([rowAt(0, distOf(ce, cn)), () => {
    for (const s of SWIRL) {
      const a = TAU * (t * 0.9 * s.sp + s.ph), rr = s.r * (0.6 + lift), h = s.hmax * lift * (0.4 + 0.6 * frac(s.ph * 7 + t * 0.21 * s.sp));
      const [x, y] = P(ce + Math.cos(a) * rr, cn + Math.sin(a) * rr, h), d = distOf(ce, cn);
      ctx.drawImage(LEAF_SPR[s.pal][d < 26 ? 3 : 2][Math.sin(a * 3) > 0 ? 1 : 0], Math.round(x), Math.round(y));
    }
  }]);
}

/* ================= Steam from the street: the downtown steam pipes venting through a manhole ================= */
let PUFFS;                                          // dithered puff sprites by radius
function makePuffs() {
  return [2, 3, 4, 6, 8, 11].map(r => {
    const p = pix(r * 2 + 1, r * 2 + 1);
    for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) {
      const q = Math.hypot(i, j) / r;
      if (q < 1 && bayer(i + r, j + r) < (1 - q) * 0.9) p.set(i + r, j + r, '#c8ccd8');
    }
    return { c: p.done(), r };
  });
}
function drawSteam(t, list) {
  const g = gustAt(t);
  VENTS.forEach((V, vi) => list.push([rowAt(0, distOf(V.e, V.n)) - 0.2, () => {
    for (let k = 0; k < V.n0; k++) {
      const { cyc, u } = cycle(t, 160, k / V.n0 + vi * 0.37), w = hash(k + vi * 50, cyc, 4);
      const h = 0.2 + u * (V.rise - g * V.rise * 0.45), de = u * V.we * (1 + g * 3) - w * 0.6, dn = u * V.wn * (1 + g * 3) + (w - 0.5) * 1.2 * u;
      const [x, y] = P(V.e + de, V.n + dn, h), rm = (0.3 + u * 1.4) * pxm(distOf(V.e + de, V.n + dn));
      const pf = PUFFS[Math.min(PUFFS.length - 1, PUFFS.findIndex(p => p.r >= rm) < 0 ? PUFFS.length - 1 : PUFFS.findIndex(p => p.r >= rm))];
      ctx.globalAlpha = 0.34 * (1 - u) * smooth(0, 0.08, u);
      ctx.drawImage(pf.c, Math.round(x - pf.r), Math.round(y - pf.r));
    }
    ctx.globalAlpha = 1;
  }]));
}

/* ================= Pigeons on the gable; up at the strike ================= */
// They roost on the gable's steps and the balcony rail, shuffle and bob; the clock's first stroke puts them up: a
// loop round the tower, then back to their ledges one by one.
const ROOST = [[2.0, 18.05], [2.6, 18.05], [9.3, 18.05], [8.8, 18.05], [4.4, 7.8], [6.9, 7.8], [0.7, 16.05]];   // [uS, h]
function drawPigeons(t) {
  ROOST.forEach(([uS, h], i) => {
    const [rx, ry] = P(3.05, OSH.es[1] + uS, h + 0.05);
    const up = PIGEONS.up + i * 0.25, back = PIGEONS.land + i * 1.6;
    let x = rx, y = ry, fly = false;
    if (t >= up && t < back + 1.2) {
      const u = (t - up) / (back - up), a = TAU * (u * 1.25 + i / 7), rr = Math.sin(Math.PI * clamp(u, 0, 1));
      const cx = 238 + 30 * Math.sin(a), cy = 52 + 12 * Math.cos(a) - 18 * rr;
      x = lerp(rx, cx, rr); y = lerp(ry, cy, rr); fly = t < back;
    }
    const xi = Math.round(x + (fly ? 0 : (hash(i, step(t, 120), 3) < 0.15 ? 1 : 0))), yi = Math.round(y);
    if (fly) { const f = (step(t, 1920) + i) & 1; R(xi - 1, yi, 3, 1, '#b8b4b0'); R(xi - 3, yi - 1 + f * 2, 2, 1, '#d8d0c4'); R(xi + 2, yi - 1 + f * 2, 2, 1, '#d8d0c4'); }
    else { R(xi, yi - 2, 2, 2, '#6a6e7a'); dot(xi + (i % 2), yi - 3 + (hash(i, step(t, 960), 5) < 0.3 ? 1 : 0), '#8a8e9a'); }
  });
}

// pigeons on the sidewalk by us, pecking at crumbs; they flutter up when the tour comes past, and settle again
const PIGEON_UP = ['....hh......', '...hhn......', '..bbnbbbbt..', '.bBBBBBBBtt.', '..bbbbbbb...', '...f..f.....'];
const PIGEON_DN = ['............', '............', '..bbbbbbbt..', 'hbBBBBBBBtt.', 'hnbbbbbbb...', '...f..f.....'];
const PIGEON_K = { h: '#6a7280', n: '#4a7a6a', b: '#8a8e9a', B: '#5e626e', t: '#3e404a', f: '#b0584a' };
let PIGEON_SPR;
const GROUND_PIGEONS = [[31.6, 15.0, 0.1], [32.8, 15.6, 0.45], [30.8, 14.4, 0.7], [33.6, 16.4, 0.25]];
const SCATTER = [176, 196];                         // the tour walks past them
function drawGroundPigeons(t, list) {
  GROUND_PIGEONS.forEach(([pe, pn, ph], i) => {
    const wob = 0.25 * Math.sin(TAU * (2 * t / LOOP + ph)) + 0.2 * Math.sin(TAU * (7 * t / LOOP + ph * 3));
    const e = pe + wob, n = pn + 0.2 * Math.cos(TAU * (3 * t / LOOP + ph)), d = distOf(e, n), [x, y] = P(e, n, 0), s = pxm(d);
    const up = SCATTER[0] + i * 0.4, back = SCATTER[1] + i * 2.2, flying = t >= up && t < back;
    list.push([y, () => {
      let yy = y, xx = x;
      if (flying) { const u = (t - up) / (back - up), lift = Math.sin(Math.PI * u); yy = y - lift * 2.4 * s; xx = x + Math.sin(TAU * u + i) * 1.2 * s; }
      const peck = !flying && frac(t * 1.25 + ph * 3) < 0.3, dir = Math.cos(TAU * (2 * t / LOOP + ph)) > 0 ? 1 : 0;
      if (!flying) { ctx.fillStyle = "rgba(6,8,20,0.35)"; ctx.fillRect(Math.round(xx - 5), Math.round(y) + 1, 10, 1); }
      if (flying) {
        const f = (step(t, 1920) + i) & 1;
        R(xx - 3, yy - 3, 7, 2, "#8a8e9a"); R(xx - 6, yy - 4 + f * 3, 4, 1, "#a8acb8"); R(xx + 3, yy - 4 + f * 3, 4, 1, "#a8acb8");
      } else ctx.drawImage(PIGEON_SPR[peck ? 1 : 0][dir], Math.round(xx - 6), Math.round(yy - 5));
    }]);
  });
}
// a night guard's torch moving room to room inside the Old State House: a glow from one window, then the next
const TORCH = [[2.2, 4.4], [5.8, 4.4], [9.4, 4.4], [9.4, 9.4], [5.8, 9.0], [2.2, 9.4]];   // [uS, h] of the east windows it visits
function drawTorch(t) {
  const c = t % 60;                                                                          // a round every minute, 18 s of it at our end
  if (c < 30 || c >= 48) return;
  const k = Math.floor((c - 30) / 3), u = (c - 30) / 3 - k, [uS, h] = TORCH[k];
  const a = Math.sin(Math.PI * u), [x, y] = wallPt(uS + (u - 0.5) * 0.6, h + 0.3 * Math.sin(u * 7));
  ctx.globalAlpha = 0.35 * a; ctx.fillStyle = "#ffe8b0";
  const [x0, y0] = wallPt(uS - 0.6, h + 1.4), [x1, y1] = wallPt(uS + 0.6, h - 1.3);
  ctx.fillRect(Math.round(Math.min(x0, x1)) + 1, Math.round(y0) + 1, Math.round(Math.abs(x1 - x0)) - 1, Math.round(y1 - y0) - 1);
  ctx.globalAlpha = 1;
  halo(HL.torch, x, y, a);
}

/* ================= The clock's hands and the weathervane, per frame ================= */
const wallPt = (uS, h) => P(OSH.es[0] + 0.02, OSH.es[1] + uS, h);
function drawClockHands(clock) {
  const m = clock ?? 1290;                         // 9:30 pm when nobody passes the time
  for (const [a, len, col] of [[TAU * (m % 720) / 720, 0.5, GOLD[4]], [TAU * (m % 60) / 60, 0.78, GOLD[5]]]) {
    for (let k = 1; k <= 6; k++) {
      const q = k / 6, [x, y] = wallPt(CLOCK.u - Math.sin(a) * len * q, CLOCK.h + Math.cos(a) * len * q);
      dot(x, y, col);
    }
  }
  const [cx, cy] = wallPt(CLOCK.u, CLOCK.h); dot(cx, cy, GOLD[6]);
}
function drawVane(t) {
  const [x, y] = P(TWR.e, TWR.n, 34.4), g = gustAt(t), ang = 0.9 + 0.25 * wave(t, 6, 0.2) + g * 1.2 * Math.sin(TAU * t / 3), len = Math.round(7 * Math.sin(ang));
  ctx.fillStyle = GOLD[5];
  const dir = len >= 0 ? 1 : -1;
  for (let k = 0; k <= Math.abs(len); k++) ctx.fillRect(x + dir * k, y + (k > Math.abs(len) * 0.5 ? 1 : 0), 1, 1);
  if (Math.abs(len) > 2) { dot(x + dir * Math.abs(len), y - 1, GOLD[4]); dot(x + dir * Math.abs(len), y + 2, GOLD[4]); }   // the banner's swallow tail
  dot(x, y - 1, GOLD[6]);
}

/* ================= Shadows on the facade ================= */
// Two uplights on the plaza wash the east end; whoever stands between them and the wall throws a tall soft shadow on
// the brick (the tour group most of all, while the guide talks)
const UPLIGHTS = [[16.8, -1.2], [16.4, -5.6]];
function drawWallShadows(people) {
  const xa = Math.ceil(P(...OSH.es)[0]), xb = Math.floor(P(...OSH.en)[0]);
  ctx.save(); ctx.beginPath(); ctx.rect(xa, 0, xb - xa, H); ctx.clip();
  ctx.fillStyle = '#0c0408';
  for (const [pe, pn] of people) {
    if (pe < 3.4 || pe > 16) continue;
    for (const [le, ln] of UPLIGHTS) {
      if (pe > le - 0.8) continue;
      const k = (le - 3) / (le - pe), ns = ln + (pn - ln) * k, hTop = 0.3 + (1.72 - 0.3) * k, half = 0.28 * k;
      if (ns < OSH.es[1] - 0.5 || ns > OSH.en[1] + 0.5) continue;
      const [x0, y0] = wallPt(ns - OSH.es[1] - half, hTop), [x1] = wallPt(ns - OSH.es[1] + half, hTop), [, yb] = wallPt(ns - OSH.es[1], 0);
      ctx.globalAlpha = clamp(0.32 - (k - 2) * 0.04, 0.1, 0.32);
      const w = Math.max(1, Math.round(x1 - x0)), hd = Math.max(2, Math.round(w * 0.45));
      ctx.fillRect(Math.round(x0), Math.round(y0) + hd, w, Math.round(yb - y0) - hd);           // the body
      ctx.fillRect(Math.round(x0 + w * 0.28), Math.round(y0), Math.max(1, Math.round(w * 0.44)), hd + 1);   // the head
    }
  }
  ctx.globalAlpha = 1; ctx.restore();
}

/* ================= Weather plans: the city's own is dry leaves; a pick lands on the street and catches the lamps ================= */
const WEATHER = weatherPlans(['leaves']);
let WX, WX_BB;

/* ================= Prerendered state ================= */
let BIN_C, HL, SKY_C, WISPS, TOW, OSH_C, OSH_WARM, GROUND_C, SCAF_C, AD_C, PITCH, LAMPS_C, PLANTERS_C, POLE_C, VIG_C;
const STARS = [];
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.45) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [4, 5, 16], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  WINS.length = 0; STARS.length = 0;
  for (const k in LOOKS) delete LOOKS[k];
  HL = {
    moon: makeHalo(16, '#c8d0ff', 0.16, 1.4), lamp: makeHalo(30, '#ffc878', 0.16, 1.6), lampCore: makeHalo(7, '#fff2d0', 0.4, 1.3),
    tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), tail: makeHalo(3, '#ff3020', 0.5, 1.1), beam: makeHalo(30, '#fff0d0', 0.13, 1.5), wash: makeHalo(26, '#fff4e0', 0.34, 1.3), washF: makeHalo(12, '#fff4e0', 0.34, 1.3), amberWash: makeHalo(34, '#ffa030', 0.3, 1.4), torch: makeHalo(5, '#fff8e0', 0.6, 1.2), trail: makeHalo(12, '#ff3020', 0.12, 1.4),
    amber: makeHalo(12, '#ffa020', 0.4, 1.3), lantern: makeHalo(9, '#ffc060', 0.45, 1.3), flash: makeHalo(9, '#f4f8ff', 0.5, 1.2),
    sigG: makeHalo(6, '#40ff90', 0.55, 1.2), sigA: makeHalo(6, '#ffb020', 0.55, 1.2), sigR: makeHalo(6, '#ff3020', 0.55, 1.2),
    sigFar: makeHalo(3, '#ffffff', 0.5, 1.1), tdoor: makeHalo(14, '#ffd890', 0.3, 1.4), work: makeHalo(8, '#fff0c8', 0.35, 1.3),
    flood: makeHalo(40, '#ffb070', 0.08, 1.6), osh: makeHalo(70, '#ff9a50', 0.05, 1.5), upl: makeHalo(4, '#fff0d0', 0.45, 1.2),
  };
  SKY_C = renderSky();
  WISPS = [makeWisps(11, 70, 7, '#1c2448', '#4a3e58', 90), makeWisps(29, 60, 6, '#242a50', '#5a4858', 70)];
  TOW = renderTowers();
  FOOT = TOW.foot;
  OSH_C = renderOSH(0).c; OSH_WARM = renderOSH(1).c;
  GROUND_C = renderGround(TOW.foot);
  SCAF_C = renderScaffold(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  LAMPS_C = LAMPS.map(renderLamp);
  PLANTERS_C = PLANTERS.map(renderPlanter);
  POLE_C = renderPole(); BIN_C = renderBin();
  LEAF_SPR = LEAF_PALS.map(pal => LEAF_SHAPES.map(sh => [0, 1].map(fl => {
    const rows = sh.map(r => fl ? r.replace(/1/g, '2') : r);
    return sprite(rows, { 1: mixHex(pal[0], NIGHT, 0.3), 2: mixHex(pal[1], NIGHT, 0.3) });
  })));
  PUFFS = makePuffs();
  PIGEON_SPR = [PIGEON_UP, PIGEON_DN].map(rows => [sprite(rows, PIGEON_K, true), sprite(rows, PIGEON_K)]);
  MIRROR = renderMirror(); MIRROR_TMP = makeCanvas(W, H);
  // the stars: only where the sky shows between the towers
  const tx = TOW.c.getContext('2d').getImageData(0, 0, W, H0).data, ox = OSH_C.getContext('2d').getImageData(0, 0, W, H0).data, r = rng(91);
  for (let tries = 0; STARS.length < 40 && tries < 6000; tries++) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 120), o = (y * W + x) * 4;
    if (tx[o + 3] || ox[o + 3] || Math.hypot(x - MOON.x, y - MOON.y) < 9) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.35 + r() * r() * 0.65, c: r() < 0.3 ? '#ffe8c8' : '#dfe6ff' });
  }
  const lampLights = LAMPS_C.flatMap(L => L.heads.map(([x, y]) => ({ x, y: y + 18, rx: 18 + L.s, ry: 30 + L.s * 1.5 })));
  const lights = [...lampLights, { x: P(T_GLOW.e, T_GLOW.n)[0], y: 186, rx: 14, ry: 12 }];
  const land = [[186, 194], [194, 208], [H + 4, H + 4]];
  WX = { land, lights };
  WX_BB = { land, lights: [...lights, { x: BB.x + 6, y: BB.y + 24, rx: 14, ry: 12, col: '#fff0c8' }] };
  VIG_C = renderVignette();
}

/* ================= Per frame ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.45 + 0.55 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  WISPS.forEach((c, i) => { const off = scroll(t, i + 1, W), y = [2, 40][i]; ctx.drawImage(c, -off, y); ctx.drawImage(c, W - off, y); });
  halo(HL.moon, MOON.x, MOON.y, 1);
  for (let j = 0; j < 3; j++) {                                                              // jets out of Logan, high over the city, west-bound
    const u = frac(3 * t / LOOP + j / 3);
    if (u > 0.5) continue;
    const x = lerp(W + 10, -10, u / 0.5), y = 9 + j * 6 + u * 8;
    dot(x, y, '#c8ccd8'); if (frac(t * 0.8 + j * 0.3) < 0.12) dot(x + 1, y, '#ff4a40');
  }
}
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
const SIG_COL = { g: '#5cff9a', a: '#ffc040', r: '#ff4a3a' };
function drawFarSignals(t) {
  const c = SIGNAL(t + 20), [x, y] = P(-44, 5.5, 5.4);
  dot(x, y, SIG_COL[c]); halo(c === 'g' ? HL.sigG : c === 'a' ? HL.sigA : HL.sigR, x, y, 0.5);
  const [x2, y2] = P(-44, 0.5, 5.4); dot(x2, y2, SIG_COL[c]); halo(c === 'g' ? HL.sigG : c === 'a' ? HL.sigA : HL.sigR, x2, y2, 0.4);
}
function drawBillboard(t, pitch) {
  blit(SCAF_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(AD_C, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of [BB.x + 8, BB.x + BB.w - 9]) { R(lx, BB.y - 2, 2, 1, '#2a2c34'); dot(lx, BB.y - 3, '#fff0c8'); halo(HL.work, lx, BB.y + 2, 0.6 + 0.03 * wave(t, 780, lx * 0.01)); }
}
function drawOSH(t, warm) {
  halo(HL.osh, 222, 120, (0.6 + 0.4 * warm) * (0.96 + 0.04 * wave(t, 840, 0.2)));   // the floods' glow in the cold air, never quite still
  ctx.drawImage(OSH_C, 0, 0);
  if (warm > 0.004) { ctx.globalAlpha = warm; ctx.drawImage(OSH_WARM, 0, 0); ctx.globalAlpha = 1; }
}
function drawPole(t) {
  blit(POLE_C);
  const c = SIGNAL(t);
  for (const [hx, hy, r, j] of POLE_C.heads) {                                               // red, amber, green lenses top to bottom
    const on = (j === 0 && c === 'r') || (j === 1 && c === 'a') || (j === 2 && c === 'g'), col = ['#ff4a3a', '#ffc040', '#5cff9a'][j];
    ctx.fillStyle = on ? col : mixHex(col, '#0a0a0c', 0.82);
    ctx.fillRect(hx - (r >> 1), hy - (r >> 1), r, r);
    if (on) { dot(hx - (r >> 1), hy - (r >> 1), '#ffffff'); halo([HL.sigR, HL.sigA, HL.sigG][j], hx, hy, 0.95); }
  }
  for (const [txt, bx, by] of POLE_C.blades) {
    let cx = bx;
    sign(ctx, txt, cx, by, '#e8f0e8');
  }
  const walkOn = c === 'r';                                                                  // the pedestrian head: a white walker on red, an orange hand else
  dot(POLE_C.ped[0], POLE_C.ped[1], walkOn ? '#f4f4f0' : '#ff8a30'); dot(POLE_C.ped[0], POLE_C.ped[1] + 1, walkOn ? '#c8c8c4' : '#c86a20');
}
// One Boston Place's dark glass mirrors the sky behind us: thin cloud lit by the city drifting across it
let MIRROR, MIRROR_TMP;
function renderMirror() {
  const r = rng(77), blobs = [];
  for (let i = 0; i < 22; i++) blobs.push({ x: r() * W, y: r() * 190, rx: 20 + r() * 50, ry: 3 + r() * 6 });
  const p = pix(W, 190);
  for (let y = 0; y < 190; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2); }
    v *= 0.7 + 0.6 * vnoise(x / 11, y / 3, 78);
    if (v > 0.15 + bayer(x, y) * 0.45) p.set(x, y, v > 0.5 ? '#a89cb0' : '#6a6280');
  }
  return p.done();
}
function drawMirror(t) {
  const [, mx] = MIRROR_TMP, off = scroll(t, 2, W), dy = Math.round(4 * wave(t, 1, 0.1));
  mx.globalCompositeOperation = 'copy'; mx.globalAlpha = 1;
  mx.drawImage(MIRROR, off, dy); mx.globalCompositeOperation = 'source-over'; mx.drawImage(MIRROR, off - W, dy);
  mx.globalCompositeOperation = 'destination-in'; mx.drawImage(TOW.glass, 0, 0); mx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.2; ctx.drawImage(MIRROR_TMP[0], 0, 0); ctx.globalAlpha = 1;
}
const SHAD = [];
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share, clock } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = '#06070e'; ctx.fillRect(0, 0, W, H);
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, warm = warmAt(t);
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  ctx.drawImage(TOW.c, 0, 0);
  ctx.drawImage(GROUND_C, 0, 0);
  drawMirror(t);
  for (const w of WINS) if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c);
  drawFarSignals(t);
  if (billboard) drawBillboard(t, pitch);
  if (wx.leaves) drawFalling(FALL[0], 0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawOSH(t, warm);
  // the people and things on the street, nearest last
  const DL = [];
  SHAD.length = 0;
  drawGuide(t, crowd, DL);
  drawCouple(t, crowd, DL);
  drawRider(t, crowd, DL);
  drawTrainFolk(t, crowd, DL);
  drawWalkers(t, crowd, DL);
  drawExtras(t, crowd, DL);
  drawCars(t, DL);
  drawSweeper(t, DL);
  drawBike(t, crowd, DL);
  drawSteam(t, DL);
  drawSwirl(t, DL);
  drawGroundPigeons(t, DL);
  LAMPS_C.forEach(L => DL.push([rowAt(0, L.d), () => blit(L)]));
  PLANTERS_C.forEach(Pl => DL.push([rowAt(0, Pl.d), () => blit(Pl)]));
  DL.push([POLE_C.yb, () => drawPole(t)]);
  DL.push([rowAt(0, BIN_C.d), () => blit(BIN_C)]);
  drawWallShadows(SHAD);
  drawClockHands(clock);
  drawTorch(t);
  drawPigeons(t);
  drawVane(t);
  const [tdx, tdy] = P(T_GLOW.e, T_GLOW.n, 1.2); halo(HL.tdoor, tdx, tdy, 0.78 + 0.03 * wave(t, 720, 0.4));
  for (const [ue, un] of UPLIGHTS) { const [ux, uy] = P(ue, un, 0.2); R(ux - 1, uy - 1, 3, 2, '#14141a'); dot(ux, uy - 1, '#fff4dc'); halo(HL.upl, ux, uy - 1, 0.7); }
  drawSkitter(t);
  if (wx.leaves) drawFalling(FALL[1], 1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  LAMPS_C.forEach((L, i) => L.heads.forEach(([hx, hy], j) => {                               // the lanterns breathe a little, as they do
    const I = 0.94 + 0.04 * wave(t, 900, i * 0.31 + j * 0.17);
    halo(HL.lamp, hx, hy + 4, 0.78 * I); halo(HL.lampCore, hx, hy, 0.92 * I);
  }));
  if (wx.leaves) drawFalling(FALL[2], 2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'boston-old-state-house',
  name: 'Boston',
  tagline: 'Freedom Trail by lantern light',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the Green Line's green, written out: tools/seo/seo.mjs reads it from the source
  focusX: 222,
  landmark: [183, 262],                                // the east gable, the north-east corner and the tower over it
  landmarkTop: 27,                                     // the weathervane's tip
  billboard: BB,
  crowd: CROWD,
  spots: [
    { kind: 'bell', x: 204, y: 71, w: 23, h: 18 },                                          // the clock in the gable
    { kind: 'landmark', x: 238, y: 26, w: 25, h: 44 },                                      // the tower
    { kind: 'window', x: 265, y: 159, w: 11, h: 10 },                                       // the lantern by the station entrance
  ],                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { traffic: 0.24, wind: 0.22, crowd: 0.05 },
  events: [
    ...TRAINS.map(t0 => ({ t: t0, type: 'train', x: 255, gain: 0.16 })),
    ...STRIKES.map((t0, i) => ({ t: t0, type: 'bell', x: 215, gain: i ? 0.3 : 0.36 })),
    { t: BIKE.t0 + 2, type: 'chime', x: 300, gain: 0.08 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
